import { validateDemoIdentity } from "@/api/mock-data/investment-banking/identity";
import { DEMO_MCP_SERVER_CATALOG } from "@/api/mock-data/investment-banking/mcpServers";
import { buildDemoMcpServices } from "@/api/mock-data/investment-banking/resources";
import { DEMO_SCENARIOS } from "@/api/mock-data/investment-banking/scenarios";
import {
  AgentClassificationEvidence,
  AgentDiscovery,
  AgentEnvironment,
  AgentEstateGroup,
  AgentEstateSummary,
  AgentGroupDimension,
  AgentMission,
  AgentMissionConfidence,
  AgentMissionContextChange,
  AgentMissionEvidence,
  AgentMissionHistoryEntry,
  AgentMissionSource,
  AgentMissionStatus,
  AgentMissionSuggestion,
  AgentOnboardingProgress,
  AgentPolicyCoverage,
  AgentPolicyAssignment,
  AgentPolicyGap,
  AgentPolicyRecommendation,
  AgentPurpose,
  AgentScope,
  AgentSecurityStatus,
  ApplyPoliciesRequest,
  ApplyPoliciesResult,
  ApplyPolicyResult,
  AppliedPolicyCoverage,
  AttentionAgentIds,
  DiscoveredProject,
  DiscoveryStageId,
  DraftPolicy,
  DraftPolicyControl,
  EffectiveEnforcement,
  EffectiveEnforcementQuery,
  EffectivePolicySummary,
  EnforcementContext,
  EnforcementPathFact,
  EnforcementReadinessStatus,
  EstateAgent,
  EstateAgentActivity,
  EstateAgentDetail,
  EstateAgentMcpService,
  EstateAgentQuery,
  EstateAgentToolClassification,
  EstateCoverage,
  GenerateDraftPolicyRequest,
  ProjectAgentPreview,
  GOVERNED_MISSION_STATUSES,
  NativePolicy,
  NativePolicyLogic,
  OverridesPresentError,
  PaginatedEstateAgents,
  PolicyApplicability,
  PolicyApplicationPreview,
  PolicyPreviewAgent,
  PolicyCatalogItem,
  PolicyImplementation,
  PolicyImplementationPlan,
  PolicyImplementationQuery,
  PolicyImplementationsQuery,
  PolicyReview,
  PolicyReviewAgent,
  PolicyReviewPolicy,
  PolicyReviewQuery,
  PolicyTargets,
  ScopedPolicyCatalogItem,
  ScopedPolicyCatalogQuery,
  PolicyCatalogQuery,
  PolicyCategory,
  PolicyControl,
  ReapplyPolicyDefinitionRequest,
  RetryPolicyDeploymentRequest,
  SetAgentAccessRevocationRequest,
  SetAgentAccessRevocationResult,
  ScopeChangedError,
  StartAgentDiscoveryRequest,
  StartAgentOnboardingRequest,
  TestAgentEnvironmentConnectionRequest,
  TestAgentIdentityConnectionRequest,
  TestAgentEnvironmentConnectionResult,
  UpdateAgentMissionRequest,
  UpdateAgentMissionResult,
  UpdateNativePolicyRequest,
  fromEnforcementQuery,
  isCatalogEntrySelectable,
} from "@/api/temp/AgentEstateService";
import type {
  AccessPurpose,
} from "@/api/temp/McpAuthenticationService";
import {
  agentSessionScope,
  readAgentSession,
  registerAgentSessionReset,
  writeAgentSession,
} from "@/libs/agentSession";
import {
  type ApplicationPlan,
  buildApplicationPlan,
  buildReviewCohort,
  type CohortAgentInput,
  classifyCoverageChange,
  fnv1a,
  type CoverageSnapshot,
  operationCounts,
  type PlanOperationCounts,
  type PlanRecordInput,
  type RecordDestination,
  type ReviewCohort,
  tallyCoverageChanges,
} from "@/libs/estateCounts";
import { isInvestmentBankingDemo } from "@/libs/investmentBankingDemo";
import { tenantEnforcementRevision } from "@/libs/tenantEnforcement";
import {
  isFormatCheckedIdentityProvider,
  matchesSecurityStatusFilter,
} from "@/pages/AgentEstate/constants";
import {
  emptyReadinessCounts,
  tallyReadiness,
} from "@/pages/AgentEstate/enforcementReadiness/model";
import { clientProfileReferenceOf } from "@/pages/MCPRegistry/authentication/authFormModel";
import { buildAgentAccessFlow, type FlowEnforcement } from "./accessFlow";
import {
  buildImplementation,
  type DeploymentRun,
  type NativeOverride,
  runCompletesAt,
  startRun,
} from "./deployment";
import {
  agentsInProjects,
  discoveredProjectsOf,
  discoveryComplete,
  discoveryStages,
  importAgents,
} from "./discovery";
import {
  accessPathsFor,
  type EnforcementAgentInput,
  getEffectiveEnforcement,
  toPolicySummary,
  toTargetSummary,
} from "./enforcement";
import {
  getEnforcementScenario,
  matchesEnforcementFilter,
  readinessContext,
  readinessForAgent,
  type ReadinessAgent,
  registerReadinessPathResolver,
} from "./enforcementReadiness";
import { generateAgents, GeneratedAgent, getTotalAgentCount } from "./generate";
import {
  type DefinitionInput,
  materializeDefinition,
  mergePlans,
  type NativePolicyBlueprint,
  parseNativePolicyId,
  planFromBlueprints,
} from "./materialize";
import {
  AUTHORIZATION_SERVERS,
  authorizationServerOf,
  type ConnectionBlocker,
  connectionBlockerOf,
  gatewayClientFor,
  initialProfileState,
  type McpConnectivityState,
  type McpProfileState,
  mcpProfilesFor,
  seededMcpServiceIds,
  settleJobs,
} from "./mcpConnectivity";
import {
  classifiedCount,
  discoveredCount,
  DISCOVERY_DURATION_MS,
  elapsedMsFor,
  failedStageProgress,
  lifecycleFor,
  stageProgress,
  STAGE_WINDOWS,
} from "./phases";
import {
  type AgentPolicyTargets,
  matchesSelector,
  resolvePolicyTargets,
  unionCoverage,
} from "./policyTargets";
import { notifyEstateReset } from "./resetListeners";
import {
  BUSINESS_UNIT_LABELS,
  CLASSIFICATION_EVIDENCE_CATALOG,
  DRAFT_POLICY_KEYWORDS,
  getIntentPolicies,
  getMcpServerCatalog,
  McpServerDefinition,
  withConnected,
  MISSION_CANDIDATE_CATALOG,
  MISSION_CONTEXT_CHANGE_CATALOG,
  MISSION_EVIDENCE_CATALOG,
  MISSION_NAMES,
  MISSION_SUGGESTION_CATALOG,
  MISSION_SYNTHESIS,
  PLATFORM_LABELS,
  getPlatformCatalog,
  POLICY_CATALOG,
  PURPOSE_LABELS,
  getRecommendationCatalog,
  RecommendationCatalogEntry,
} from "./seed";
import {
  type AgentEstateSnapshot,
  type AgentMissionDecision,
  isAgentEstateSnapshot,
  type McpBinding,
} from "./session";
import { toolDescription } from "./toolDescriptions";

/**
 * Mock store for the AI Agent Estate onboarding preview
 * (`isNewAgentOnboarding`). There is no backend for any of this yet.
 *
 * Session storage retains environments and administrator changes across reloads.
 * Generated agents and derived summaries are rebuilt when the session is read.
 * Authentication boundaries start a new, empty estate; explicit demo/test
 * resets can still load the populated fixture.
 *
 * Three invariants worth protecting:
 *  - Agent records are never mutated. Applied policies and access suspensions
 *    live in override maps, so a progress tick can never overwrite either.
 *  - The estate is whatever discovery has found so far. Agents beyond the
 *    discovered cutoff are absent, so every count on screen is a partition of
 *    the same visible agents.
 *  - Security status is only reported for agents classification has reached.
 *    Everything between the classified and discovered cutoffs is NOT_EVALUATED,
 *    which is what makes that status reachable without inventing a permanent
 *    cohort that would break the 99 + 50 = 149 arithmetic.
 */

/**
 * A generated agent with any mission override applied. `seedPurpose` preserves
 * what discovery classified, which an override must never rewrite - the
 * classification evidence is a record of what was observed.
 */
interface EffectiveAgent extends GeneratedAgent {
  /**
   * Present only when an override is in force, so `purpose` and `seedPurpose`
   * differ. Read it as `seedPurpose ?? purpose` - absent means the two agree.
   */
  seedPurpose?: AgentPurpose;
}

/**
 * One wizard discovery run. Held in memory only: it is the wizard's working
 * state, and a reload discards the wizard along with it.
 */
interface PendingDiscovery {
  /** The environment onboarding will create - fixed now so agent ids match. */
  environment: AgentEnvironment;
  agents: GeneratedAgent[];
  /** Fixed by the run, so computed once rather than on every poll. */
  projects: DiscoveredProject[];
}

/**
 * The connected environment used by explicit demo scenarios and test resets.
 * Ordinary sessions restore their saved environments or start at day zero.
 *
 * `startedAt` is back-dated past the discovery window so the run reads as
 * finished, and the id is fixed rather than clock-derived so deep links and
 * specs are deterministic.
 */
const SEEDED_ENVIRONMENT_ID = "agent-environment-seed";

const buildSeededEnvironment = (): AgentEnvironment => ({
  id: SEEDED_ENVIRONMENT_ID,
  name: "Enterprise Foundry",
  platform: "MICROSOFT_FOUNDRY",
  identityProvider: "MICROSOFT_ENTRA_ID",
  environmentType: "PRODUCTION",
  health: "HEALTHY",
  startedAt: "2026-09-15T09:00:00.000Z",
  totalAgents: getTotalAgentCount(),
});

/** One saved connection configuration's OAuth client reference. */
export interface ClientProfileConnection {
  serviceId: string;
  serviceName: string;
  connectionId: string;
  connectionName: string;
  profileId: string;
  /** Uses the gateway identity, which in turn refers to this profile. */
  inherited: boolean;
  purposes: AccessPurpose[];
  environmentName?: string;
  agentIds: string[];
}

export interface GatewayProfileReference {
  profileId: string;
  gatewayName: string;
  issuerName: string;
  environmentName?: string;
}

/** One agent before and after a hypothetical assignment. */
interface AgentProjection {
  before: CoverageSnapshot;
  after: CoverageSnapshot;
  beforeGaps: AgentPolicyGap[];
}

/** What the mock's failure scenario reports; shown in the activity panel only. */
const MOCK_FAILURE_MESSAGE =
  "The provider stopped responding while agent context was being analyzed.";

export class AgentEstateStore {
  private loadedScope?: string;
  private environments: AgentEnvironment[] = [];

  /** Generated exactly once per environment, then read-only. */
  private agentsByEnvironmentId = new Map<string, GeneratedAgent[]>();

  /** The agents the wizard selected per environment; absent imports all. */
  private importedAgentIds = new Map<string, string[]>();

  private discoveries = new Map<string, PendingDiscovery>();

  /** Connection profiles, evidence and running checks per MCP Service id. */
  private mcpConnectivityState = new Map<string, McpConnectivityState>();
  /**
   * Test-only failure scenario, per environment: the stage the run stopped at
   * and how far into the run that was. Deliberately never persisted - a failed
   * run is a scenario to inspect, not state to carry into the next session.
   */
  private failedOnboarding = new Map<
    string,
    { stageId: DiscoveryStageId; elapsedMs: number; withMessage: boolean }
  >();

  /** Assignments retain policy identity and never imply complete coverage. */
  private assignments = new Map<string, AgentPolicyAssignment[]>();
  private activity = new Map<string, EstateAgentActivity[]>();

  private appliedAt = new Map<string, string>();

  /** Agents suspended by Kill Switch. Orthogonal to security status. */
  private revokedAgentIds = new Set<string>();

  /**
   * MCP services an administrator connected to an agent, keyed by agent id.
   * Additive to what discovery reports; never a policy. The one record of a
   * manual connection: Access & Protection writes here, so the agent's detail,
   * summary counts, enforcement and policy targets all see it.
   */
  private mcpBindings = new Map<string, McpBinding[]>();

  /** Mission decisions taken this session, keyed by agent id. */
  private missionDecisions = new Map<string, AgentMissionDecision>();

  /** Session drafts remain available for policy application after a refresh. */
  private draftPolicies = new Map<string, DraftPolicy>();

  /**
   * Deployment runs per applied definition, keyed by `implementationKey`.
   * Each native policy's state is derived from these and the clock, so a
   * deployment in progress survives reloads and never restarts.
   */
  private deploymentRuns = new Map<string, DeploymentRun[]>();

  /** Individual overrides of native policies, keyed by native policy id. */
  private nativeOverrides = new Map<string, NativeOverride>();

  /** Each implementation with the blueprints it was built from. */
  private implementationMemo = new Map<
    string,
    { implementation: PolicyImplementation; blueprints: NativePolicyBlueprint[] }
  >();

  /** Bumped by every write; part of the summary memo key. */
  private mutationVersion = 0;
  /** Confirmed applies by the review they came from, so a repeat is idempotent. Memory only. */
  private appliedByPreview = new Map<string, ApplyPoliciesResult>();

  private snapshotMemo?: { key: string; agents: EstateAgentDetail[] };

  private summaryMemo = new Map<string, AgentEstateSummary>();
  /** Per service: whether its default connection profile blocks enforcement. */
  private connectionMemo = new Map<string, ConnectionBlocker>();
  /** One build per policy per estate state; every view of it reads this. */
  private enforcementMemo = new Map<string, EnforcementAgentInput[]>();

  private ensureSession(): void {
    const scope = agentSessionScope(isInvestmentBankingDemo());
    if (scope === this.loadedScope) return;
    this.clearMemory();
    this.loadedScope = scope;
    const saved = readAgentSession("estate", scope, isAgentEstateSnapshot);
    if (!saved) return;
    this.environments = saved.environments;
    this.importedAgentIds = new Map(saved.importedAgentIds ?? []);
    this.mcpConnectivityState = new Map(saved.mcpAuthenticationState ?? []);
    this.agentsByEnvironmentId = new Map(
      saved.environments.map(environment => [
        environment.id,
        importAgents(
          generateAgents(environment),
          this.importedAgentIds.get(environment.id),
        ),
      ]),
    );
    this.assignments = new Map(saved.assignments);
    this.activity = new Map(saved.activity);
    this.appliedAt = new Map(saved.appliedAt);
    this.revokedAgentIds = new Set(saved.revokedAgentIds);
    this.missionDecisions = new Map(saved.missionDecisions);
    this.draftPolicies = new Map(saved.draftPolicies);
    this.deploymentRuns = new Map(saved.deploymentRuns ?? []);
    this.nativeOverrides = new Map(saved.nativeOverrides ?? []);
    this.mcpBindings = new Map(saved.mcpBindings ?? []);
  }

  private persist(): void {
    if (this.loadedScope === undefined) return;
    writeAgentSession("estate", this.loadedScope, {
      environments: this.environments,
      assignments: [...this.assignments],
      activity: [...this.activity],
      appliedAt: [...this.appliedAt],
      revokedAgentIds: [...this.revokedAgentIds],
      missionDecisions: [...this.missionDecisions],
      draftPolicies: [...this.draftPolicies],
      deploymentRuns: [...this.deploymentRuns],
      nativeOverrides: [...this.nativeOverrides],
      importedAgentIds: [...this.importedAgentIds],
      mcpAuthenticationState: [...this.mcpConnectivityState],
      mcpBindings: [...this.mcpBindings],
    } satisfies AgentEstateSnapshot);
  }

  /** Authentication reset: clear memory without writing the old user's state. */
  clearSession(): void {
    this.loadedScope = undefined;
    this.clearMemory();
  }

  // ---------------------------------------------------------------- reads

  listEnvironments(): AgentEnvironment[] {
    this.ensureSession();
    return this.environments.map(environment => ({ ...environment }));
  }

  /** The environment an agent is visible in, for a read by agent id alone. */
  environmentOf(agentId: string, now = Date.now()): string | undefined {
    this.ensureSession();
    return this.environments.find(environment =>
      this.visibleAgent(environment.id, agentId, now),
    )?.id;
  }

  /** The agent by id alone, from whichever environment has it - as the live API. */
  findAgent(agentId: string, now = Date.now()): EstateAgentDetail | undefined {
    const environmentId = this.environmentOf(agentId, now);
    return environmentId ? this.getAgent(environmentId, agentId, now) : undefined;
  }

  getProgress(
    environmentId: string,
    now = Date.now(),
  ): AgentOnboardingProgress | undefined {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    if (!environment) return undefined;

    const failure = this.failedOnboarding.get(environmentId);
    // A failed run stops where it stopped: its clock no longer advances.
    const at = failure
      ? new Date(environment.startedAt).getTime() + failure.elapsedMs
      : now;
    const elapsedMs = elapsedMsFor(environment, at);
    const total = environment.totalAgents;
    const discovered = this.discoveredFor(environment, at);
    const classified = this.classifiedFor(environment, at);

    const visible = this.agentsFor(environmentId)
      .slice(0, discovered)
      .map(agent => this.withGovernedPurpose(agent));
    const technologies = new Set<string>();
    let secure = 0;
    let notSecure = 0;
    let notEvaluated = 0;
    let withRecommendations = 0;
    const enforcement = emptyReadinessCounts();
    const readiness = readinessContext(undefined, now);

    visible.forEach(agent => {
      technologies.add(agent.connectedResource);
      tallyReadiness(enforcement, readinessForAgent(agent, readiness).status);
      const status = this.effectiveStatus(agent, classified);
      if (status === "SECURE") secure += 1;
      else if (status === "NOT_EVALUATED") notEvaluated += 1;
      else {
        notSecure += 1;
        if (this.remainingRecommendations(agent).length > 0)
          withRecommendations += 1;
      }
    });

    const lifecycle = failure ? "FAILED" : lifecycleFor(elapsedMs);
    const mcpServiceCount = mcpServiceIdsOf(this.agentsFor(environmentId)).size;
    const startedAtMs = new Date(environment.startedAt).getTime();

    return {
      environment: { ...environment },
      lifecycle,
      totalAgents: total,
      discoveredCount: discovered,
      classifiedCount: classified,
      secureCount: secure,
      notSecureCount: notSecure,
      notEvaluatedCount: notEvaluated,
      technologyCount: technologies.size,
      recommendationCount: withRecommendations,
      enforcement,
      percentComplete: Math.min(
        100,
        Math.round((elapsedMs / DISCOVERY_DURATION_MS) * 100),
      ),
      elapsedMs,
      ...(lifecycle === "FAILED"
        ? {
            completedAt: new Date(startedAtMs + elapsedMs).toISOString(),
            ...(failure?.withMessage === false
              ? {}
              : { failureMessage: MOCK_FAILURE_MESSAGE }),
          }
        : lifecycle === "COMPLETE"
          ? {
              completedAt: new Date(
                startedAtMs + DISCOVERY_DURATION_MS,
              ).toISOString(),
            }
          : {}),
      stages: failure
        ? failedStageProgress(total, failure.stageId, mcpServiceCount)
        : stageProgress(total, elapsedMs, mcpServiceCount),
    };
  }

  /**
   * The category cards. Takes the same filters as the agent list, so clicking
   * a metric on the summary header narrows the cards and the table together
   * rather than leaving the two describing different sets of agents.
   */
  getSummary(
    params: {
      environmentId: string;
      dimension: AgentGroupDimension;
    } & Omit<EstateAgentQuery, "environmentId">,
    now = Date.now(),
  ): AgentEstateSummary | undefined {
    this.ensureSession();
    const { environmentId, dimension } = params;
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    if (!environment) return undefined;

    // Memo key includes both cutoffs, so a summary is recomputed when the
    // estate actually changed - not on every poll tick. Without `classified`,
    // a summary taken mid-run would be served after agents had been classified
    // but before `discovered` next moved.
    const discovered = this.discoveredFor(environment, now);
    const classified = this.classifiedFor(environment, now);
    const key = [
      environmentId,
      dimension,
      discovered,
      classified,
      this.mutationVersion,
      getEnforcementScenario(),
      tenantEnforcementRevision(),
      queryFingerprint(params),
    ].join(":");
    const memoized = this.summaryMemo.get(key);
    if (memoized) return memoized;

    const groups = new Map<string, AgentEstateGroup>();
    const groupPolicyIds = new Map<string, Set<string>>();
    let total = 0;
    let secure = 0;
    let notSecure = 0;
    let notEvaluated = 0;
    let protectedTotal = 0;
    let suspended = 0;
    let recommendations = 0;
    const enforcement = emptyReadinessCounts();
    const context = readinessContext(undefined, now);
    const attention = emptyAttentionIds();

    const agents = outsideRegistry(params, environmentId)
      ? []
      : this.visibleAgents(environmentId, now);
    agents.forEach(agent => {
      const status = this.effectiveStatus(agent, classified);
      const remainingRecommendations = this.remainingRecommendations(agent);
      const recommendationCount = remainingRecommendations.length;
      const isSuspended = this.revokedAgentIds.has(agent.id);
      const readiness = readinessForAgent(agent, context).status;
      if (
        !this.matchesQuery(agent, status, recommendationCount, readiness, params)
      )
        return;

      total += 1;
      if (status === "SECURE") secure += 1;
      else if (status === "NOT_EVALUATED") notEvaluated += 1;
      else notSecure += 1;
      addAttention(attention, agent.id, status, readiness);
      const isProtected = status === "SECURE" && readiness === "READY";
      if (isProtected) protectedTotal += 1;
      if (isSuspended) suspended += 1;
      tallyReadiness(enforcement, readiness);
      const isActionable = status === "NOT_SECURE" && recommendationCount > 0;
      if (isActionable) recommendations += 1;

      const value = groupValue(agent, dimension, status);
      let group = groups.get(value);
      if (!group) {
        group = {
          key: `${dimension}:${value}`,
          dimension,
          value,
          totalAgents: 0,
          secureCount: 0,
          notSecureCount: 0,
          notEvaluatedCount: 0,
          securePercent: 0,
          protectedCount: 0,
          accessSuspendedCount: 0,
          recommendationCount: 0,
          recommendedPolicyCount: 0,
          // Settled once the group's agents are all counted, below.
          recommendationState: "NONE",
          enforcement: emptyReadinessCounts(),
          attentionIds: emptyAttentionIds(),
        };
        groups.set(value, group);
        groupPolicyIds.set(value, new Set());
      }
      group.totalAgents += 1;
      addAttention(group.attentionIds!, agent.id, status, readiness);
      if (status === "SECURE") group.secureCount += 1;
      else if (status === "NOT_EVALUATED") group.notEvaluatedCount += 1;
      else group.notSecureCount += 1;
      if (isProtected) group.protectedCount = (group.protectedCount ?? 0) + 1;
      if (isSuspended) group.accessSuspendedCount += 1;
      tallyReadiness(group.enforcement, readiness);
      if (isActionable) {
        group.recommendationCount += 1;
        const policyIds = groupPolicyIds.get(value)!;
        remainingRecommendations.forEach(policy => policyIds.add(policy.id));
        group.recommendedPolicyCount = policyIds.size;
      }
    });

    groups.forEach(group => {
      group.securePercent = percent(group.secureCount, group.totalAgents);
      // A gap made only of agents TrustAI has not evaluated has no basis for a
      // recommendation yet - distinct from "evaluated, nothing suitable".
      group.recommendationState =
        group.recommendedPolicyCount > 0
          ? "AVAILABLE"
          : group.notSecureCount === 0 && group.notEvaluatedCount > 0
            ? "INSUFFICIENT_INFORMATION"
            : "NONE";
    });

    // Groups that still need work first, then by size - the estate is triaged
    // by reading down the list rather than hunting for a coloured accent.
    const orderedGroups = [...groups.values()].sort(
      (left, right) =>
        unprotectedIn(right) - unprotectedIn(left) ||
        right.notSecureCount - left.notSecureCount ||
        right.notEvaluatedCount - left.notEvaluatedCount ||
        right.totalAgents - left.totalAgents,
    );

    const summary: AgentEstateSummary = {
      environmentId,
      dimension,
      totalAgents: total,
      secureCount: secure,
      notSecureCount: notSecure,
      notEvaluatedCount: notEvaluated,
      securePercent: percent(secure, total),
      protectedCount: protectedTotal,
      accessSuspendedCount: suspended,
      recommendationCount: recommendations,
      enforcement,
      groups: orderedGroups,
      attentionIds: attention,
    };

    // Eight dimensions times a handful of filter combinations; clear rather
    // than grow unbounded.
    if (this.summaryMemo.size > 40) this.summaryMemo.clear();
    this.summaryMemo.set(key, summary);
    return summary;
  }

  listAgents(query: EstateAgentQuery, now = Date.now()): PaginatedEstateAgents {
    this.ensureSession();
    const pageNo = Math.max(1, Number(query.pageNo ?? 1));
    const pageSize = Math.min(100, Math.max(1, Number(query.pageSize || 25)));
    const environment = this.environments.find(
      item => item.id === query.environmentId,
    );
    if (!environment || outsideRegistry(query, environment.id))
      return { items: [], pageNo, pageSize, total: 0 };

    const classified = this.classifiedFor(environment, now);
    const matched: EstateAgent[] = [];
    const context = readinessContext(undefined, now);

    this.visibleAgents(query.environmentId, now).forEach(agent => {
      const status = this.effectiveStatus(agent, classified);
      const recommendationCount = this.remainingRecommendations(agent).length;
      const readiness = readinessForAgent(agent, context);
      if (
        !this.matchesQuery(
          agent,
          status,
          recommendationCount,
          readiness.status,
          query,
        )
      )
        return;
      matched.push(
        this.toSummary(
          agent,
          status,
          recommendationCount,
          environment,
          readiness,
        ),
      );
    });

    const sorted = sortAgents(matched, query.sortBy, query.sortOrder);
    // `usePaginateQuery` sends a 1-based page number.
    const start = (pageNo - 1) * pageSize;
    return {
      items: sorted.slice(start, start + pageSize),
      pageNo,
      pageSize,
      total: sorted.length,
    };
  }

  getAgent(
    environmentId: string,
    agentId: string,
    now = Date.now(),
  ): EstateAgentDetail | undefined {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    if (!environment) return undefined;

    const agent = this.visibleAgent(environmentId, agentId, now);
    if (!agent) return undefined;

    const classified = this.classifiedFor(environment, now);
    const status = this.effectiveStatus(agent, classified);
    const appliedPolicies = this.assignmentsFor(agent, environment);
    const mcpServices = this.servicesFor(
      agent,
      status,
      appliedPolicies,
    );

    const detail: EstateAgentDetail = {
      ...this.toSummary(
        agent,
        status,
        this.remainingRecommendations(agent).length,
        environment,
      ),
      description: `${agent.purposeDescription} operating in ${agent.environmentType.toLowerCase()} against ${agent.connectedResource}.`,
      model: agent.model,
      mission: this.missionFor(agent, environment, classified),
      classificationEvidence: this.evidenceFor(agent, environment),
      appliedPolicies,
      mcpServices,
      policyGaps: status === "NOT_EVALUATED" ? [] : this.gapsFor(agent),
      recentActivity: this.activityFor(agent, environment),
    };
    const enforcement = this.agentEnforcement(agent, environment, now);
    if (enforcement?.effectivePolicies.length)
      detail.effectivePolicies = enforcement.effectivePolicies;
    detail.accessFlow = buildAgentAccessFlow(
      detail,
      this.resolveRecommendations(
        environmentId,
        { kind: "AGENTS", agentIds: [agentId] },
        now,
        { withFacts: false },
      ),
      enforcement?.flow,
    );
    return detail;
  }

  /**
   * One Intent Policy's enforcement in one context. Every view - agent, group,
   * policy, access path - is `getEffectiveEnforcement` over the same inputs.
   */
  getEffectiveEnforcement(
    query: EffectiveEnforcementQuery,
    now = Date.now(),
  ): EffectiveEnforcement | undefined {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === query.environmentId,
    );
    const policy = getIntentPolicies().find(item => item.id === query.policyId);
    const context = fromEnforcementQuery(query);
    if (!environment || !policy || !context) return undefined;
    return getEffectiveEnforcement(
      policy,
      context,
      this.enforcementInputs(policy.id, environment, now),
    );
  }

  /**
   * The agents an Intent Policy maps to, as the enforcement selector reads
   * them. `applied` comes from the same assignments `applyPolicies` writes, so
   * applying the policy is what makes it effective - nowhere else says so.
   *
   * Must not call `getAgent`: that builds the access flow, which reads this.
   */
  private enforcementInputs(
    policyId: string,
    environment: AgentEnvironment,
    now: number,
  ): EnforcementAgentInput[] {
    const entry = getRecommendationCatalog().find(item => item.id === policyId);
    if (!entry) return [];
    const classified = this.classifiedFor(environment, now);
    const undeployed = this.undeployedTargets(environment, policyId, now);
    const key = [
      policyId,
      environment.id,
      this.mutationVersion,
      this.discoveredFor(environment, now),
      classified,
      [...undeployed].join(","),
    ].join(":");
    const memoized = this.enforcementMemo.get(key);
    if (memoized) return memoized;
    const inputs = this.visibleAgents(environment.id, now)
      .filter(agent => matchesSelector(agent, entry))
      .map(agent => {
        const status = this.effectiveStatus(agent, classified);
        const assignments = this.assignmentsFor(agent, environment);
        return {
          id: agent.id,
          name: agent.name,
          businessUnit: agent.businessUnit,
          demoKey: agent.demoKey,
          applied: assignments.some(item => item.id === policyId),
          groupKeys: ENFORCEMENT_GROUP_DIMENSIONS.map(
            dimension =>
              `${dimension}:${groupValue(agent, dimension, status)}`,
          ),
          mcpServices: this.servicesFor(
            agent,
            status,
            assignments,
          ),
          undeployed,
        };
      });
    if (this.enforcementMemo.size > 20) this.enforcementMemo.clear();
    this.enforcementMemo.set(key, inputs);
    return inputs;
  }

  /**
   * What an Intent Policy would enforce for these agents once applied. A
   * recommendation shows the counts; a review also shows the enforcement
   * points, so `detail` adds the server, tool and data-control breakdown.
   */
  private intentFieldsFor(
    policyId: string,
    agentIds: string[],
    environment: AgentEnvironment,
    now: number,
    { detail = false }: { detail?: boolean } = {},
  ) {
    const policy = getIntentPolicies().find(item => item.id === policyId);
    if (!policy) return {};
    const wanted = new Set(agentIds);
    const preview = agentIds.length
      ? getEffectiveEnforcement(
          policy,
          { type: "policy" },
          this.enforcementInputs(policyId, environment, now).filter(agent =>
            wanted.has(agent.id),
          ),
          { assumeApplied: true },
        )
      : undefined;
    return {
      intent: { allow: policy.allow, restrict: policy.restrict },
      enforcementTargets: preview ? toTargetSummary(preview) : undefined,
      ...(detail && preview
        ? {
            enforcementPreview: {
              mcpServers: preview.mcpServers,
              dataSources: preview.dataSources,
            },
          }
        : {}),
    };
  }

  /**
   * The agent's own Intent Policies - in effect or still recommended - and
   * the access-path hops the applied ones are enforced through. Undefined when no Intent Policy maps to
   * the agent at all.
   */
  private agentEnforcement(
    agent: EffectiveAgent,
    environment: AgentEnvironment,
    now: number,
  ):
    | { effectivePolicies: EffectivePolicySummary[]; flow: FlowEnforcement }
    | undefined {
    const policies = getIntentPolicies().filter(policy => {
      const entry = getRecommendationCatalog().find(
        item => item.id === policy.id,
      );
      return !!entry && matchesSelector(agent, entry);
    });
    if (!policies.length) return undefined;
    const effectivePolicies: EffectivePolicySummary[] = [];
    const flow: FlowEnforcement = { policies: [] };
    policies.forEach(policy => {
      const inputs = this.enforcementInputs(policy.id, environment, now);
      const own = inputs.find(item => item.id === agent.id);
      const context: EnforcementContext = { type: "agent", agentId: agent.id };
      const view = getEffectiveEnforcement(policy, context, inputs);
      if (!own || !view) return;
      // Listed before it is applied too, so the agent's page shows where the
      // policy will be enforced; only an applied policy marks the flow.
      effectivePolicies.push(toPolicySummary(view));
      if (!view.applied) return;
      flow.policies.push({
        policyId: policy.id,
        policyName: policy.name,
        agentStatus: view.status,
        hopsByPath: Object.fromEntries(
          accessPathsFor(agent.id, own.mcpServices).map(path => [
            path.id,
            getEffectiveEnforcement(
              policy,
              { type: "accessPath", agentId: agent.id, pathId: path.id },
              inputs,
            )?.path?.hops ?? [],
          ]),
        ),
      });
    });
    return { effectivePolicies, flow };
  }

  /**
   * Recommendations for any scope - a category, a bulk selection or one agent.
   *
   * Every entry whose selector matches at least one agent in scope is offered,
   * with `appliesTo` counting only the agents it actually matches. Nothing is
   * invented: a scope no entry matches returns an empty list, which is what
   * routes the administrator to Browse Policies or Custom Policy instead.
   */
  resolveRecommendations(
    environmentId: string,
    scope: AgentScope,
    now = Date.now(),
    /**
     * Target counts and readiness per recommendation - what the cards show.
     * Callers that only need which policies are recommended (the access flow,
     * the catalog, the review) skip them.
     */
    { withFacts = true }: { withFacts?: boolean } = {},
  ): AgentPolicyRecommendation[] {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    const scoped = this.resolveScope(environmentId, scope, now);
    if (!scoped.length || !environment) return [];
    const remaining = this.recommendedIdsByAgent(scoped, environment, now);
    const services = withFacts
      ? this.servicesByAgent(scoped, environment, now)
      : undefined;
    const context = readinessContext(undefined, now);

    const resolved = getRecommendationCatalog()
      .map((entry): AgentPolicyRecommendation | undefined => {
        const matched = scoped.filter(agent =>
          remaining.get(agent.id)?.has(entry.id),
        );
        if (!matched.length) return undefined;
        const { selector: _selector, ...recommendation } = entry;
        return {
          ...recommendation,
          appliesTo: {
            agentCount: matched.length,
            label: composeScopeLabel(matched, entry),
          },
          ...(services && {
            targets: unionCoverage(
              [...this.targetsByAgent(entry.id, matched, services).values()].filter(
                item => item.applicable,
              ),
            ),
            enforcementReadiness: tallyAgents(matched, context),
          }),
          ...this.intentFieldsFor(
            entry.id,
            matched.map(agent => agent.id),
            environment,
            now,
          ),
        } satisfies AgentPolicyRecommendation;
      })
      .filter((entry): entry is AgentPolicyRecommendation => !!entry);

    return resolved.sort(
      (left, right) =>
        strengthRank(right) - strengthRank(left) ||
        right.appliesTo.agentCount - left.appliesTo.agentCount ||
        left.name.localeCompare(right.name),
    );
  }

  /**
   * The policies already assigned across a scope - the same assignments
   * `previewPolicies` reports as ALREADY_ASSIGNED, so the drawer's coverage and
   * its review can never disagree. Each policy carries the agent ids it covers,
   * letting the caller union overlapping policies rather than summing them.
   */
  resolveCoverage(
    environmentId: string,
    scope: AgentScope,
    now = Date.now(),
  ): AgentPolicyCoverage {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    const scoped = this.resolveScope(environmentId, scope, now);
    if (!environment) return { scopeAgentCount: 0, policies: [] };

    const byPolicy = new Map<string, { name: string; agentIds: string[] }>();
    scoped.forEach(agent =>
      this.assignmentsFor(agent, environment).forEach(assignment => {
        const entry = byPolicy.get(assignment.id) ?? {
          name: assignment.name,
          agentIds: [],
        };
        entry.agentIds.push(agent.id);
        byPolicy.set(assignment.id, entry);
      }),
    );

    const policies = [...byPolicy].map(
      ([policyId, { name, agentIds }]): AppliedPolicyCoverage => ({
        policyId,
        name: this.findPolicy(policyId)?.name ?? name,
        state: "ACTIVE",
        agentIds,
        controls: this.controlsFor(policyId),
        ...this.intentFieldsFor(policyId, agentIds, environment, now),
      }),
    );

    return {
      scopeAgentCount: scoped.length,
      policies: policies.sort(
        (left, right) =>
          right.agentIds.length - left.agentIds.length ||
          left.name.localeCompare(right.name),
      ),
    };
  }

  /**
   * Each agent's MCP servers, as its detail page would list them. Built once
   * per request: they depend on the agent alone, not on the policy asked about.
   */
  private servicesByAgent(
    agents: EffectiveAgent[],
    environment: AgentEnvironment,
    now: number,
  ): Map<string, EstateAgentMcpService[]> {
    const classified = this.classifiedFor(environment, now);
    return new Map(
      agents.map(agent => [
        agent.id,
        this.servicesFor(
          agent,
          this.effectiveStatus(agent, classified),
          this.assignmentsFor(agent, environment),
        ),
      ]),
    );
  }

  /** What the engine still recommends per not-yet-secure agent, advisory included. */
  private recommendedIdsByAgent(
    agents: EffectiveAgent[],
    environment: AgentEnvironment,
    now: number,
  ): Map<string, Set<string>> {
    const classified = this.classifiedFor(environment, now);
    return new Map(
      agents
        .filter(
          agent => this.effectiveStatus(agent, classified) === "NOT_SECURE",
        )
        .map(agent => [
          agent.id,
          new Set(
            this.remainingRecommendations(agent, {
              includeAdvisory: true,
            }).map(entry => entry.id),
          ),
        ]),
    );
  }

  /** One policy on each agent, keyed by agent id. */
  private targetsByAgent(
    policyId: string,
    agents: EffectiveAgent[],
    services: Map<string, EstateAgentMcpService[]>,
  ): Map<string, AgentPolicyTargets> {
    const draft = this.draftPolicies.get(policyId);
    return new Map(
      agents.map(agent => [
        agent.id,
        resolvePolicyTargets(
          policyId,
          agent,
          services.get(agent.id) ?? [],
          draft?.technology,
        ),
      ]),
    );
  }

  /**
   * RECOMMENDED when the engine offers it for this scope, APPLICABLE when it
   * reaches at least one agent, otherwise NOT_APPLICABLE with the first
   * agent's reason. The catalog, the review and the preview all read this.
   */
  private applicabilityOf(
    agents: EffectiveAgent[],
    byAgent: Map<string, AgentPolicyTargets>,
    recommended: boolean,
  ) {
    const applicableAgents = agents.filter(
      agent => byAgent.get(agent.id)?.applicable,
    );
    const applicability: PolicyApplicability = recommended
      ? "RECOMMENDED"
      : applicableAgents.length
        ? "APPLICABLE"
        : "NOT_APPLICABLE";
    const firstMiss = [...byAgent.values()].find(item => !item.applicable);
    return {
      applicableAgents,
      applicability,
      miss:
        applicability === "NOT_APPLICABLE"
          ? {
              applicabilityReason:
                firstMiss?.reason ??
                "No agents in the current selection were found.",
              requirement: firstMiss?.requirement,
            }
          : undefined,
    };
  }

  /**
   * Who a set of policies would reach, by agent id. The review and the preview
   * both read this, so the two cannot disagree about who is targeted, who is
   * outside the recommendations' scope, or whose applicability is unknown.
   * `expectedCoverageChanges` comes from projecting the targeted agents through
   * the same gap evaluator the dashboard uses.
   */
  private cohortFor(input: {
    environment: AgentEnvironment;
    scope: AgentScope;
    scoped: EffectiveAgent[];
    policyIds: string[];
    byPolicy: Map<string, Map<string, AgentPolicyTargets>>;
    classified: number;
    requestedInspectedId?: string;
  }): {
    cohort: ReviewCohort;
    pendingByAgent: Map<string, string[]>;
    projections: Map<string, AgentProjection>;
  } {
    const { environment, scope, scoped, policyIds, byPolicy, classified } =
      input;
    const pendingByAgent = new Map<string, string[]>();
    // Name order is the review table's order, so "the first targeted agent" -
    // the inspector's deterministic default - is the row a reader sees first.
    const ordered = [...scoped].sort((left, right) =>
      left.name.localeCompare(right.name),
    );
    const projections = new Map<string, AgentProjection>();
    const agents: CohortAgentInput[] = ordered.map(agent => {
      const held = new Set(
        this.assignmentsFor(agent, environment).map(item => item.id),
      );
      // One pass over the selected policies per agent.
      const applicable: string[] = [];
      const pending: string[] = [];
      let unresolved = false;
      policyIds.forEach(id => {
        const targets = byPolicy.get(id)?.get(agent.id);
        if (targets?.unresolved) unresolved = true;
        if (!targets?.applicable) return;
        applicable.push(id);
        if (!held.has(id)) pending.push(id);
      });
      if (pending.length) {
        pendingByAgent.set(agent.id, pending);
        projections.set(
          agent.id,
          this.projectAgent(agent, classified, held, pending),
        );
      }
      return {
        id: agent.id,
        applicablePolicyIds: applicable,
        pendingPolicyIds: pending,
        applicabilityUnresolved: unresolved,
      };
    });
    const cohort = buildReviewCohort({
      entry:
        scope.kind === "AGENTS" && scope.agentIds.length === 1
          ? "SINGLE_AGENT"
          : "MULTI_AGENT",
      scopeKind: scope.kind,
      agents,
      selectedRecommendationIds: policyIds,
      recommendationSelection: policyIds.length ? "user" : "none",
      agentSelectionActive: scope.kind === "AGENTS" && scope.agentIds.length > 1,
      requestedInspectedId: input.requestedInspectedId,
    });
    cohort.expectedCoverageChanges = tallyCoverageChanges(
      cohort.targetedAgentIds.map(agentId => ({
        agentId,
        before: projections.get(agentId)!.before,
        after: projections.get(agentId)!.after,
      })),
    );
    return { cohort, pendingByAgent, projections };
  }

  /**
   * The one application plan: each distinct native policy record the selected
   * definitions would generate, deduplicated by its logical id, with what will
   * happen to it and which agents depend on it. Review totals, execution and
   * result reconciliation all read this, so they cannot count different things.
   *
   * Dependents come from each agent's own targets - never a product of every
   * agent, server and tool - and the agents a record ALREADY serves come from
   * the agents that have the definition today, so a shared record's impact on
   * agents outside the review is known before anything is applied.
   */
  private applicationPlanFor(
    environment: AgentEnvironment,
    definitions: Array<{ policyId: string; agents: EffectiveAgent[] }>,
    cohortAgentIds: string[],
    now: number,
  ): ApplicationPlan {
    const records: PlanRecordInput[] = [];
    const assignments: Array<{ agentId: string; policyId: string }> = [];
    const targeted = new Map<string, EffectiveAgent>();
    definitions.forEach(({ policyId, agents }) => {
      agents.forEach(agent => {
        targeted.set(agent.id, agent);
        assignments.push({ agentId: agent.id, policyId });
      });
      const definition = this.definitionFor(environment, policyId);
      if (!definition || !agents.length) return;

      const existing = new Map(
        (
          this.implementationFor(environment, policyId, now)?.groups.flatMap(
            group => group.policies,
          ) ?? []
        ).map(policy => [policy.id, policy] as const),
      );
      const idsFor = (agent: EffectiveAgent) =>
        this.blueprintsFor(definition, [agent], environment, now);

      // Who already depends on each record: every agent that has the
      // definition today, wherever they are in the estate.
      const served = new Map<string, Set<string>>();
      this.assignedAgents(environment, policyId, now).forEach(agent =>
        idsFor(agent).forEach(blueprint =>
          addTo(served, blueprint.id, agent.id),
        ),
      );

      const dependents = new Map<string, Set<string>>();
      const destinations = new Map<string, RecordDestination>();
      agents.forEach(agent =>
        idsFor(agent).forEach(blueprint => {
          addTo(dependents, blueprint.id, agent.id);
          destinations.set(blueprint.id, blueprint.target);
        }),
      );
      destinations.forEach((destination, id) => {
        const record = existing.get(id);
        records.push({
          id,
          definitionId: policyId,
          destination,
          dependentAgentIds: [...(dependents.get(id) ?? [])],
          existenceKnown: true,
          existing: record
            ? {
                dependentAgentIds: [...(served.get(id) ?? [])],
                state: record.state,
                overridden: record.source === "MANUALLY_MODIFIED",
              }
            : undefined,
        });
      });
    });
    return buildApplicationPlan({
      records,
      definitionIds: definitions.map(item => item.policyId),
      targetedAgentIds: [...targeted.keys()],
      cohortAgentIds,
      assignments,
    });
  }

  /**
   * The All Policies tab, read against a scope: every catalog policy with its
   * applicability. RECOMMENDED only when the recommendation engine offers it
   * for this scope, so the rationale shown is always a real one.
   */
  resolvePolicyCatalog(
    params: ScopedPolicyCatalogQuery,
    now = Date.now(),
  ): ScopedPolicyCatalogItem[] {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === params.environmentId,
    );
    const catalog = this.listPolicyCatalog(params);
    if (!environment) return [];
    const scoped = this.resolveScope(params.environmentId, params.scope, now);
    const recommended = new Map(
      this.resolveRecommendations(params.environmentId, params.scope, now, {
        withFacts: false,
      }).map(item => [item.id, item]),
    );
    const services = this.servicesByAgent(scoped, environment, now);
    const context = readinessContext(undefined, now);
    return catalog.map((item): ScopedPolicyCatalogItem => {
      const byAgent = this.targetsByAgent(item.id, scoped, services);
      const recommendation = recommended.get(item.id);
      const { applicableAgents, applicability, miss } = this.applicabilityOf(
        scoped,
        byAgent,
        !!recommendation,
      );
      return {
        ...item,
        applicability,
        ...(miss ?? {
          targets: unionCoverage(
            applicableAgents.map(agent => byAgent.get(agent.id)!),
          ),
          enforcement: tallyAgents(applicableAgents, context),
        }),
        ...(recommendation && {
          recommendationStrength: recommendation.strength,
          whyRecommended: recommendation.whyRecommended,
        }),
      };
    });
  }

  /**
   * The canonical Policy Review workspace. One or more policies against one
   * scope: what each does, whether it is recommended or merely applicable,
   * which agents it reaches and how ready they are, and - for one viewed agent
   * - exactly which servers, tools and data sources it acts on.
   */
  getPolicyReview(params: PolicyReviewQuery, now = Date.now()): PolicyReview {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === params.environmentId,
    );
    if (!environment) throw new Error("Unknown environment");
    const scoped = this.resolveScope(params.environmentId, params.scope, now);
    const policyIds = [...new Set(params.policyIds)];
    const recommended = new Map(
      this.resolveRecommendations(params.environmentId, params.scope, now, {
        withFacts: false,
      }).map(item => [item.id, item]),
    );
    const services = this.servicesByAgent(scoped, environment, now);
    const byPolicy = new Map(
      policyIds.map(id => [id, this.targetsByAgent(id, scoped, services)]),
    );
    const remaining = this.recommendedIdsByAgent(scoped, environment, now);
    const { cohort, pendingByAgent } = this.cohortFor({
      environment,
      scope: params.scope,
      scoped,
      policyIds,
      byPolicy,
      classified: this.classifiedFor(environment, now),
      requestedInspectedId: params.agentId,
    });

    const policies = policyIds.map((id): PolicyReviewPolicy => {
      const found = this.findPolicy(id);
      if (!found) throw new Error("Unknown policy");
      const entry = getRecommendationCatalog().find(item => item.id === id);
      const catalog = POLICY_CATALOG.find(item => item.id === id);
      const draft = this.draftPolicies.get(id);
      const intent = getIntentPolicies().find(item => item.id === id);
      const recommendation = recommended.get(id);
      const { applicableAgents, applicability, miss } = this.applicabilityOf(
        scoped,
        byPolicy.get(id)!,
        !!recommendation,
      );
      const unassigned = applicableAgents.filter(
        agent =>
          !this.assignmentsFor(agent, environment).some(
            item => item.id === id,
          ),
      );
      const assigned = applicableAgents.length - unassigned.length;
      const controls = this.controlsFor(id);
      return {
        policyId: id,
        name: found.name,
        category:
          entry?.category ??
          catalog?.category ??
          draft?.category ??
          "BUSINESS_RULES",
        isDraft: !entry && !catalog,
        intentBased: !!intent,
        frameworks: entry?.frameworks ?? catalog?.frameworks ?? [],
        ...(recommendation && {
          recommendation: {
            strength: recommendation.strength,
            rationale: recommendation.whyRecommended,
          },
        }),
        applicability,
        ...miss,
        summary:
          entry?.outcome ?? catalog?.description ?? draft?.intentSummary ?? "",
        intent: intent
          ? { allow: intent.allow, restrict: intent.restrict }
          : {
              allow: controls
                .filter(item => item.sense === "ALLOW")
                .map(item => item.label),
              restrict: controls
                .filter(item => item.sense === "BLOCK")
                .map(item => item.label),
            },
        controls,
        policyState:
          assigned === 0
            ? "NOT_APPLIED"
            : assigned === applicableAgents.length
              ? "APPLIED"
              : "PARTIALLY_APPLIED",
        enforcementMethod: entry
          ? entry.enforcement.enforcementLayer
          : undefined,
        implementationPlan: this.planFor(environment, id, unassigned, now),
      };
    });

    const context = readinessContext(undefined, now);
    const agents: PolicyReviewAgent[] = scoped
      .map(agent => {
        const applying = policyIds.filter(
          id => byPolicy.get(id)!.get(agent.id)?.applicable,
        );
        return {
          id: agent.id,
          name: agent.name,
          applicability: applying.some(id => remaining.get(agent.id)?.has(id))
            ? ("RECOMMENDED" as const)
            : applying.length
              ? ("APPLICABLE" as const)
              : ("NOT_APPLICABLE" as const),
          policyIds: applying,
          pendingPolicyIds: pendingByAgent.get(agent.id) ?? [],
          applicabilityUnresolved:
            applying.length === 0 &&
            policyIds.some(id => byPolicy.get(id)!.get(agent.id)?.unresolved),
          readiness: readinessForAgent(agent, context),
          targets: unionCoverage(
            applying.map(id => byPolicy.get(id)!.get(agent.id)!),
          ),
        };
      })
      .sort(
        (left, right) =>
          Number(left.applicability === "NOT_APPLICABLE") -
            Number(right.applicability === "NOT_APPLICABLE") ||
          left.name.localeCompare(right.name),
      );

    const isApplicable = (agent: PolicyReviewAgent) =>
      agent.applicability !== "NOT_APPLICABLE";
    // The inspected agent is one the policies reach: the requested one when a
    // policy reaches it, else the cohort's deterministic pick (the first
    // targeted agent), else the first agent that already has them. A stale or
    // out-of-scope id never survives to draw a graph with nothing to show.
    const viewed =
      agents.find(agent => agent.id === params.agentId && isApplicable(agent)) ??
      agents.find(agent => agent.id === cohort.inspectedAgentId) ??
      agents.find(isApplicable) ??
      agents[0];
    // Readiness over who the policies would newly reach; when nothing is
    // pending (an applied policy being viewed) it is the agents that have it.
    // Never the whole scope: an agent no policy reaches has no readiness to
    // report here.
    const targeted = new Set(cohort.targetedAgentIds);
    const enforcementScope = targeted.size ? "TARGETED" : "ASSIGNED";
    const enforcement = emptyReadinessCounts();
    agents
      .filter(agent =>
        enforcementScope === "TARGETED"
          ? targeted.has(agent.id)
          : isApplicable(agent),
      )
      .forEach(agent => tallyReadiness(enforcement, agent.readiness.status));

    return {
      scopeAgentCount: scoped.length,
      policies,
      agents,
      viewedAgentId: viewed?.id,
      cohort,
      enforcementScope,
      targets: viewed
        ? viewed.policyIds.map((policyId): PolicyTargets => {
            const {
              applicable: _applicable,
              controlsBySource: _controls,
              requirement: _requirement,
              reason: _reason,
              ...target
            } = byPolicy.get(policyId)!.get(viewed.id)!;
            return { policyId, ...target };
          })
        : [],
      enforcement,
    };
  }

  /** Allow/block controls for a recommendation, catalog entry or draft. */
  private controlsFor(policyId: string): PolicyControl[] {
    const controls =
      getRecommendationCatalog().find(entry => entry.id === policyId)
        ?.controls ??
      POLICY_CATALOG.find(item => item.id === policyId)?.controls ??
      this.draftPolicies.get(policyId)?.controls ??
      [];
    return controls.map(({ sense, label }) => ({ sense, label }));
  }

  /** The All Policies tab. Pure filter - no store state is read or written. */
  listPolicyCatalog(query: PolicyCatalogQuery): PolicyCatalogItem[] {
    const search = String(query.q ?? "")
      .trim()
      .toLowerCase();
    return [
      ...POLICY_CATALOG,
      ...getRecommendationCatalog()
        .filter(item => item.demoScenario)
        .map(item => ({
          id: item.id,
          name: item.name,
          category: item.category,
          description: item.outcome,
          technology: item.technology,
          scope: item.whyRecommended,
          frameworks: item.frameworks,
          controls: item.controls,
        })),
    ].filter(item => {
      if (query.category && item.category !== query.category) return false;
      // An "Any" policy applies whatever technology is being filtered for.
      if (
        query.technology &&
        item.technology !== "Any" &&
        item.technology !== query.technology
      ) {
        return false;
      }
      if (
        search &&
        ![item.name, item.description, item.scope].some(field =>
          field.toLowerCase().includes(search),
        )
      ) {
        return false;
      }
      return true;
    });
  }

  /**
   * Mocked draft generation for the Custom Policy tab.
   *
   * Deliberately a keyword pass rather than anything that pretends to reason:
   * every control names the phrase that produced it, so a viewer can see
   * exactly why the draft says what it says. Deterministic in the intent text,
   * so regenerating the same sentence yields the same draft.
   */
  generateDraftPolicy(params: GenerateDraftPolicyRequest): DraftPolicy {
    this.ensureSession();
    const intent = String(params.intent ?? "").trim();
    const haystack = intent.toLowerCase();

    const controls: DraftPolicyControl[] = [];
    const seen = new Set<string>();
    const categories: PolicyCategory[] = [];
    let technology: DraftPolicy["technology"] = "Any";

    DRAFT_POLICY_KEYWORDS.forEach(rule => {
      const found = rule.match.exec(haystack);
      if (!found) return;
      categories.push(rule.category);
      if (rule.technology) technology = rule.technology;
      if (seen.has(rule.label)) return;
      seen.add(rule.label);
      controls.push({
        sense: rule.sense,
        label: rule.label,
        derivedFrom: found[0],
      });
    });

    // Never hand back an empty draft: an intent nothing matched still tells us
    // the administrator wants this gated, so say that and show the raw phrase.
    const unmatchedTerms = controls.length ? [] : [intent].filter(Boolean);
    if (!controls.length) {
      controls.push({
        sense: "BLOCK",
        label: "Require explicit approval for this action",
        derivedFrom: intent,
      });
    }

    const draft: DraftPolicy = {
      id: `draft-${fnv1a(intent).toString(36)}`,
      intent,
      intentSummary: summariseIntent(intent, controls),
      suggestedName: suggestDraftName(controls, technology),
      category: mostFrequent(categories) ?? "BUSINESS_RULES",
      technology,
      controls,
      unmatchedTerms,
    };

    this.draftPolicies.set(draft.id, draft);
    this.persist();
    return draft;
  }

  // --------------------------------------------------------------- writes

  /**
   * A token containing "fail" is the demo's unreachable-platform switch: §21
   * asks for a recoverable error to be reachable on stage, and a sentinel keeps
   * the expected configuration passing every time while still giving the retry
   * path something to retry.
   */
  testConnection(
    params: TestAgentEnvironmentConnectionRequest,
  ): TestAgentEnvironmentConnectionResult {
    const platform = PLATFORM_LABELS[params.platform] ?? "the agent registry";
    const missing = [
      params.connectionName,
      params.account,
      params.clientId,
      params.authorizationToken,
    ].some(value => !String(value ?? "").trim());

    if (missing) {
      return {
        success: false,
        message:
          "Complete every required connection field, then test the connection again.",
      };
    }

    if (String(params.authorizationToken).toLowerCase().includes("fail")) {
      return {
        success: false,
        message: `Unable to connect to ${platform}. The authorization token was rejected.`,
      };
    }

    return {
      success: true,
      message: `Connection successful. TrustLogix can access the ${platform} environment.`,
    };
  }

  /**
   * Reusing the platform connection needs nothing further proved: those
   * credentials just passed a live test one step earlier, and asking the
   * administrator to re-enter them only to check them again would be the
   * product forgetting what it was told.
   */
  testIdentityConnection(
    params: TestAgentIdentityConnectionRequest,
  ): TestAgentEnvironmentConnectionResult {
    // Providers with no connector: a format check, never an authentication.
    if (isFormatCheckedIdentityProvider(params.provider))
      return validateDemoIdentity(params);
    if (params.provider === "AWS_IAM")
      return { success: false, message: "AWS IAM is coming soon." };
    const provider = "Microsoft Entra ID";
    const connected = {
      success: true,
      message: `Identity provider connected. TrustLogix can now resolve identity context for onboarded agents.`,
    };

    if (params.reusedFromPlatformConnection) return connected;

    const missing = [
      params.tenantId,
      params.clientId,
      params.clientSecret,
    ].some(value => !String(value ?? "").trim());

    if (missing) {
      return {
        success: false,
        message:
          "Complete every required identity field, then test the connection again.",
      };
    }

    if (String(params.clientSecret).toLowerCase().includes("fail")) {
      return {
        success: false,
        message: `Unable to connect to ${provider}. The client secret was rejected.`,
      };
    }

    return connected;
  }

  startOnboarding(params: StartAgentOnboardingRequest): AgentEnvironment {
    this.ensureSession();
    const platform = getPlatformCatalog().find(
      entry => entry.id === params.platform,
    );
    if (!platform || !isCatalogEntrySelectable(platform))
      throw new Error("This platform integration is coming soon.");
    if (
      params.identityProvider === "AWS_IAM" ||
      (isFormatCheckedIdentityProvider(params.identityProvider) &&
        !isInvestmentBankingDemo())
    )
      throw new Error("This identity provider is not available.");
    const discovery = params.discoveryId
      ? this.discoveries.get(params.discoveryId)
      : undefined;
    // The listing run is consumed by the start that used it. Refusing a repeat
    // is what keeps a retry after a lost response from creating a second
    // registry for the same connection.
    if (params.discoveryId && !discovery)
      throw new Error(
        "This project listing is no longer available. Check the dashboard for an onboarding already in progress before starting again.",
      );
    const base = discovery?.environment ?? {
      ...pendingEnvironment(params),
      totalAgents: getTotalAgentCount(),
    };
    if (params.projectIds?.length === 0)
      throw new Error("Select at least one project to onboard.");
    const agents = agentsInProjects(
      discovery?.agents ?? generateAgents(base),
      params.projectIds,
    );
    const projectIds = params.projectIds ? new Set(params.projectIds) : null;
    const projects = discovery?.projects
      .filter(project => !projectIds || projectIds.has(project.id))
      .map(({ id, name }) => ({ id, name }));
    const environment: AgentEnvironment = {
      ...base,
      // The wizard edits these after discovery ran; the request is current.
      identityProvider: params.identityProvider,
      businessContext: params.businessContext,
      health:
        params.identityProvider === "CONFIGURE_LATER" ? "WARNING" : "HEALTHY",
      startedAt: new Date().toISOString(),
      totalAgents: agents.length,
      ...(projects ? { projects } : {}),
    };

    this.environments = [
      ...this.environments.filter(item => item.id !== environment.id),
      environment,
    ];
    this.agentsByEnvironmentId.set(environment.id, agents);
    if (params.projectIds)
      this.importedAgentIds.set(
        environment.id,
        agents.map(agent => agent.id),
      );
    else this.importedAgentIds.delete(environment.id);
    if (params.discoveryId) this.discoveries.delete(params.discoveryId);
    this.invalidate();
    return { ...environment };
  }

  // ------------------------------------------------ wizard discovery

  startDiscovery(params: StartAgentDiscoveryRequest): { discoveryId: string } {
    this.ensureSession();
    const environment: AgentEnvironment = {
      ...pendingEnvironment({
        ...params,
        identityProvider: "CONFIGURE_LATER",
      }),
      totalAgents: getTotalAgentCount(),
    };
    const discoveryId = `discovery-${environment.id}-${Date.now()}`;
    const agents = generateAgents(environment);
    this.discoveries.set(discoveryId, {
      environment,
      agents,
      projects: discoveredProjectsOf(agents),
    });
    return { discoveryId };
  }

  getDiscovery(discoveryId: string, now = Date.now()): AgentDiscovery {
    this.ensureSession();
    const discovery = this.requireDiscovery(discoveryId);
    const complete = this.discoveryIsComplete(discovery, now);
    return {
      discoveryId,
      status: complete ? "COMPLETE" : "RUNNING",
      stages: discoveryStages(
        this.discoveryElapsedMs(discovery, now),
        discovery.projects.length,
      ),
      projects: complete ? discovery.projects.map(p => ({ ...p })) : [],
    };
  }

  /** One project's agents, read-only - the wizard's preview, not the import. */
  listProjectAgents(
    discoveryId: string,
    projectId: string,
    now = Date.now(),
  ): ProjectAgentPreview {
    this.ensureSession();
    const discovery = this.requireDiscovery(discoveryId);
    if (!this.discoveryIsComplete(discovery, now))
      throw new Error("The project list is still loading.");
    if (!discovery.projects.some(project => project.id === projectId))
      throw new Error("This project is not part of the connection.");
    return {
      agents: agentsInProjects(discovery.agents, [projectId]).map(agent => ({
        id: agent.id,
        name: agent.name,
      })),
      complete: true,
    };
  }

  private discoveryElapsedMs(discovery: PendingDiscovery, now: number) {
    return Math.max(
      0,
      now - new Date(discovery.environment.startedAt).getTime(),
    );
  }

  private discoveryIsComplete(discovery: PendingDiscovery, now: number) {
    return discoveryComplete(this.discoveryElapsedMs(discovery, now));
  }

  /**
   * Every saved MCP connection configuration that references an OAuth client
   * profile, directly or through the gateway identity - the relationships
   * client-profile usage and impact are derived from. Never a stored count.
   */
  clientProfileConnections(now = Date.now()): ClientProfileConnection[] {
    this.ensureSession();
    const { environment } = readinessContext(undefined, now);
    // Agents per service, built once rather than rescanned per connection.
    const agentIdsByService = new Map<string, string[]>();
    this.onboardedAgents(now).forEach(agent =>
      this.reachedServices(agent).forEach(service => {
        const ids = agentIdsByService.get(service.id);
        if (ids) ids.push(agent.id);
        else agentIdsByService.set(service.id, [agent.id]);
      }),
    );
    const names = new Map(
      [...getMcpServerCatalog(), ...DEMO_MCP_SERVER_CATALOG].map(server => [
        server.id,
        server.name,
      ]),
    );
    return seededMcpServiceIds().flatMap(serviceId =>
      mcpProfilesFor(serviceId).flatMap(connection => {
        const state = this.profileState(serviceId, connection.id, now);
        const reference = state.configured
          ? clientProfileReferenceOf(state.formValues)
          : undefined;
        if (!reference) return [];
        const profileId =
          reference.kind === "PROFILE"
            ? reference.profileId
            : gatewayClientFor(environment, authorizationServerOf(serviceId))
                ?.clientProfileId;
        if (!profileId) return [];
        return [
          {
            serviceId,
            serviceName: names.get(serviceId) ?? serviceId,
            connectionId: connection.id,
            connectionName: connection.name,
            profileId,
            inherited: reference.kind === "GATEWAY",
            purposes: reference.purposes,
            environmentName: environment.name,
            agentIds: agentIdsByService.get(serviceId) ?? [],
          },
        ];
      }),
    );
  }

  /** The tenant gateway's default client per issuer it is registered with. */
  gatewayClientProfileReferences(now = Date.now()): GatewayProfileReference[] {
    this.ensureSession();
    const { environment } = readinessContext(undefined, now);
    return AUTHORIZATION_SERVERS.flatMap(server => {
      const client = gatewayClientFor(environment, server);
      return client?.clientProfileId
        ? [
            {
              profileId: client.clientProfileId,
              gatewayName: client.displayName,
              issuerName: server.name,
              environmentName: environment.name,
            },
          ]
        : [];
    });
  }

  private requireDiscovery(discoveryId: string): PendingDiscovery {
    const discovery = this.discoveries.get(discoveryId);
    if (!discovery) throw new Error("This discovery run is no longer available.");
    return discovery;
  }

  private onboardedAgents(now: number): EffectiveAgent[] {
    return this.environments.flatMap(environment =>
      this.visibleAgents(environment.id, now),
    );
  }

  private connectivityContext(
    serviceId: string,
    profileId: string,
    now: number,
  ) {
    return {
      serviceId,
      profileId,
      now,
      environment: readinessContext(undefined, now).environment,
    };
  }

  /** A profile as stored, with any finished checks settled into evidence. */
  private profileState(
    serviceId: string,
    profileId: string,
    now: number,
  ): McpProfileState {
    const stored =
      this.mcpConnectivityState.get(serviceId)?.[profileId] ??
      initialProfileState(serviceId, profileId, undefined, now);
    const settled = settleJobs(
      stored,
      this.connectivityContext(serviceId, profileId, now),
    );
    if (settled !== stored) {
      this.writeProfile(serviceId, profileId, settled);
      this.persist();
    }
    return settled;
  }

  private writeProfile(
    serviceId: string,
    profileId: string,
    profile: McpProfileState,
  ): void {
    this.mcpConnectivityState.set(serviceId, {
      ...this.mcpConnectivityState.get(serviceId),
      [profileId]: profile,
    });
    // A connection changed, so every agent's setup aggregate may have too.
    this.connectionMemo.delete(serviceId);
    this.mutationVersion += 1;
    this.snapshotMemo = undefined;
    this.summaryMemo.clear();
    this.enforcementMemo.clear();
  }

  /**
   * The gateway-to-service connection facts behind an agent's setup: one per
   * MCP service it uses, judged from that service's default connection profile.
   * A missing or failing connection is a confirmed blocker; a saved-but-untested
   * one is configured, not proven.
   */
  connectionPathsForReadiness(
    agent: ReadinessAgent,
    now: number,
  ): EnforcementPathFact[] {
    if (!("platform" in agent)) return [];
    // The estate's designed fully-set-up groups (the test tier and the named
    // demo agents) stay so: connection fixtures describe the rest.
    if (agent.environmentType === "TEST" || agent.demoScenario) return [];
    // Only services with a curated connection profile have connection facts.
    // The generic fallback profiles exist so every discovered service can be
    // opened, not to assert what the gateway can reach: judging them would
    // make most of the estate "setup required" for a reason nobody modelled.
    const curated = new Set(seededMcpServiceIds());
    return this.reachedServices(agent as GeneratedAgent)
      .filter(service => curated.has(service.id))
      .map(service => {
      let blocker = this.connectionMemo.get(service.id);
      if (!blocker) {
        const profileId = mcpProfilesFor(service.id)[0]?.id ?? "default";
        blocker = connectionBlockerOf(
          this.profileState(service.id, profileId, now),
        );
        this.connectionMemo.set(service.id, blocker);
      }
      const base = {
        kind: "mcpConnection" as const,
        serviceId: service.id,
        serviceName: service.name,
      };
      return blocker === "OK"
        ? { ...base, state: "CONFIGURED" as const }
        : {
            ...base,
            state: "SETUP_REQUIRED" as const,
            reason:
              blocker === "FAILED"
                ? ("mcpConnectionFailed" as const)
                : ("mcpConnectionMissing" as const),
          };
    });
  }

  disconnect(id: string): { success: boolean } {
    this.ensureSession();
    const existed = this.environments.some(
      environment => environment.id === id,
    );
    this.environments = this.environments.filter(
      environment => environment.id !== id,
    );

    const agents = this.agentsByEnvironmentId.get(id) ?? [];
    agents.forEach(agent => {
      this.assignments.delete(agent.id);
      this.activity.delete(agent.id);
      this.appliedAt.delete(agent.id);
      this.revokedAgentIds.delete(agent.id);
      this.missionDecisions.delete(agent.id);
    });
    this.agentsByEnvironmentId.delete(id);
    this.draftPolicies.clear();
    this.invalidate();
    return { success: existed };
  }

  previewPolicies(
    params: ApplyPoliciesRequest,
    now = Date.now(),
  ): PolicyApplicationPreview {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === params.environmentId,
    );
    if (!environment) throw new Error("Unknown environment");
    const scoped = this.resolveScope(params.environmentId, params.scope, now);
    const classified = this.classifiedFor(environment, now);
    const services = this.servicesByAgent(scoped, environment, now);
    const policyIds = [...new Set(params.policyIds)];
    // The same target resolution the review reads, so an agent the review
    // calls not applicable is exactly one the preview excludes.
    const byPolicy = new Map(
      policyIds.map(id => {
        if (!this.findPolicy(id)) throw new Error("Unknown policy");
        return [id, this.targetsByAgent(id, scoped, services)];
      }),
    );
    // Who is targeted, who is outside scope, and what each targeted agent is
    // projected to become - decided once, by the shared cohort, then rendered.
    const { cohort, pendingByAgent, projections } = this.cohortFor({
      environment,
      scope: params.scope,
      scoped,
      policyIds,
      byPolicy,
      classified,
    });
    const pendingAgentsByPolicy = new Map<string, EffectiveAgent[]>();
    const policiesBase = policyIds.map(id => {
      const policy = this.findPolicy(id)!;
      const recommendation = getRecommendationCatalog().find(
        item => item.id === id,
      );
      const catalog = POLICY_CATALOG.find(item => item.id === id);
      const draft = this.draftPolicies.get(id);
      const targets = byPolicy.get(id)!;
      const eligible = scoped.filter(
        agent => targets.get(agent.id)?.applicable,
      );
      const agents = eligible.filter(agent =>
        pendingByAgent.get(agent.id)?.includes(id),
      );
      pendingAgentsByPolicy.set(id, agents);
      return {
        policyId: id,
        policyName: policy.name,
        reason:
          recommendation?.whyRecommended ??
          catalog?.scope ??
          draft?.intentSummary ??
          "",
        outcome:
          recommendation?.outcome ??
          catalog?.description ??
          draft?.intentSummary ??
          "",
        ...this.intentFieldsFor(
          id,
          agents.map(agent => agent.id),
          environment,
          now,
          { detail: true },
        ),
        implementationPlan: this.planFor(environment, id, agents, now),
        agents: agents.map(agent => {
          const projection = projections.get(agent.id)!;
          return {
            id: agent.id,
            name: agent.name,
            gaps: projection.beforeGaps.filter(
              gap => gap.recommendationId === id,
            ),
            statusBefore: projection.before.status,
            statusAfter: projection.after.status,
            remainingGapCount: projection.after.gapCount,
          };
        }),
        excludedAgentCount: scoped.length - eligible.length,
        exclusions: scoped
          .filter(agent => !agents.some(target => target.id === agent.id))
          .map(agent => ({
            id: agent.id,
            name: agent.name,
            reason: eligible.some(target => target.id === agent.id)
              ? ("ALREADY_ASSIGNED" as const)
              : ("NOT_APPLICABLE" as const),
          })),
        alreadyAppliedCount: eligible.length - agents.length,
      };
    });
    // One plan of distinct records, each with the agents that depend on it.
    const applicationPlan = this.applicationPlanFor(
      environment,
      policyIds.map(id => ({
        policyId: id,
        agents: pendingAgentsByPolicy.get(id) ?? [],
      })),
      cohort.candidateAgentIds,
      now,
    );
    // The legacy per-definition plans, told what the operations actually are.
    const policies = policiesBase.map(policy => ({
      ...policy,
      implementationPlan: policy.implementationPlan
        ? withOperations(
            policy.implementationPlan,
            operationCounts(applicationPlan, policy.policyId),
          )
        : undefined,
    }));
    const targetedAgents = scoped.filter(agent => pendingByAgent.has(agent.id));
    const previewAgents: PolicyPreviewAgent[] = targetedAgents.map(agent => {
      const projection = projections.get(agent.id)!;
      return {
        id: agent.id,
        name: agent.name,
        policyIds: pendingByAgent.get(agent.id)!,
        before: projection.before,
        after: projection.after,
        change: classifyCoverageChange(projection.before, projection.after),
      };
    });
    // Readiness of the agents policies would be assigned to, so the review can
    // say whether what it creates will actually control runtime access. Zero
    // when nothing would be assigned: an empty target set has no readiness, and
    // borrowing the whole scope's would describe agents nothing touches.
    const context = readinessContext(undefined, now);
    const enforcement = emptyReadinessCounts();
    targetedAgents.forEach(agent =>
      tallyReadiness(enforcement, readinessForAgent(agent, context).status),
    );
    const plans = policies.flatMap(policy => policy.implementationPlan ?? []);
    const snapshot = {
      scopeAgentCount: scoped.length,
      affectedAgentCount: cohort.targetedAgentIds.length,
      newlySecureCount: cohort.expectedCoverageChanges?.newlyCovered ?? 0,
      cohort,
      agents: previewAgents,
      policies,
      enforcement,
      implementationPlan: plans.length
        ? withOperations(mergePlans(plans), operationCounts(applicationPlan))
        : undefined,
      applicationPlan,
    };
    return {
      ...snapshot,
      id: String(
        fnv1a(JSON.stringify([this.mutationVersion, classified, snapshot])),
      ),
    };
  }

  applyPolicies(
    params: ApplyPoliciesRequest,
    now = Date.now(),
  ): ApplyPoliciesResult | undefined {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === params.environmentId,
    );
    if (!environment || params.policyIds.some(id => !this.findPolicy(id)))
      return undefined;
    // A repeated apply of an already-applied review - a double click, or a
    // retry after a slow response - is the same request, so it gets the same
    // answer. Without this, the first apply changes the world, the second
    // arrives with a preview id that no longer matches, and a completed apply
    // is reported to the reader as a scope change.
    const done = params.previewId
      ? this.appliedByPreview.get(params.previewId)
      : undefined;
    if (done) return done;

    const preview = this.previewPolicies(params, now);
    if (params.previewId && params.previewId !== preview.id)
      throw new ScopeChangedError();
    const plan = preview.applicationPlan!;
    const coverageBefore = this.coverageFor(params.environmentId, now);
    const appliedAt = new Date(now).toISOString();
    const readiness = readinessContext(undefined, now);

    // Assign: exactly what was reviewed, and never twice.
    preview.policies.forEach(policy =>
      policy.agents.forEach(target => {
        const agent = this.visibleAgent(params.environmentId, target.id, now)!;
        const held = this.assignmentsFor(agent, environment);
        if (held.some(item => item.id === policy.policyId)) return;
        const history = this.activityFor(agent, environment);
        this.assignments.set(agent.id, [
          ...held,
          { id: policy.policyId, name: policy.policyName, appliedAt },
        ]);
        this.appliedAt.set(agent.id, appliedAt);
        this.activity.set(agent.id, [
          {
            id: `${agent.id}-${policy.policyId}-${now}`,
            occurredAt: appliedAt,
            kind: "POLICY_APPLIED",
            action: "Policy applied",
            service: agent.connectedResource,
            decision: "RECORDED",
            policyId: policy.policyId,
            policyName: policy.policyName,
          },
          ...history,
        ]);
      }),
    );

    // Deploy: the records the plan says will change, and only those. A record
    // carrying an individual override is left alone; one already in place for
    // exactly these agents is a successful no-op, not another run.
    const operationById = new Map(
      plan.records.map(record => [record.id, record.operation] as const),
    );
    preview.policies.forEach(policy => {
      const definition = this.definitionFor(environment, policy.policyId);
      if (!definition || !policy.agents.length) return;
      const newAgents = policy.agents.flatMap(
        target =>
          this.visibleAgent(params.environmentId, target.id, now) ?? [],
      );
      const touched = this.blueprintsFor(
        definition,
        newAgents,
        environment,
        now,
      ).filter(child => {
        const operation = operationById.get(child.id);
        return operation !== "SKIPPED_OVERRIDE" && operation !== "UNCHANGED";
      });
      if (!touched.length) return;
      this.appendRun(
        environment,
        policy.policyId,
        startRun("APPLY", touched, now, {
          createdIds: touched
            .filter(child => operationById.get(child.id) === "CREATE")
            .map(child => child.id),
        }),
      );
    });
    this.invalidate();

    // Read the world back: what the targeted agents ARE now, judged by the same
    // evaluator as the preview, so "expected" and "actual" are comparable.
    const classified = this.classifiedFor(environment, now);
    const actual = tallyCoverageChanges(
      (preview.agents ?? []).map(agent => {
        const live = this.visibleAgent(params.environmentId, agent.id, now)!;
        const { status, gaps } = this.evaluate(live, classified);
        return {
          agentId: agent.id,
          before: agent.before,
          after: { status, gapCount: gaps.length },
        };
      }),
    );
    const newlyCovered = new Set(actual.agentIdsByChange.NEWLY_COVERED);
    const results: ApplyPolicyResult[] = preview.policies.map(policy => ({
      policyId: policy.policyId,
      policyName: policy.policyName,
      appliedAt,
      scopeAgentCount: policy.agents.length,
      newlySecureCount: policy.agents.filter(agent =>
        newlyCovered.has(agent.id),
      ).length,
      enforcementTargets: policy.enforcementTargets,
      enforcement: policy.agents.reduce((counts, target) => {
        const agent = this.visibleAgent(params.environmentId, target.id, now);
        if (agent)
          tallyReadiness(counts, readinessForAgent(agent, readiness).status);
        return counts;
      }, emptyReadinessCounts()),
      contextLabel:
        policy.agents.length === 1 ? policy.agents[0].name : undefined,
    }));

    const result: ApplyPoliciesResult = {
      appliedAt,
      results,
      sourceAgentCount: preview.scopeAgentCount,
      targetedAgentCount: preview.affectedAgentCount,
      // Deprecated alias of `targetedAgentCount`, kept for older readers.
      scopeAgentCount: preview.affectedAgentCount,
      newlySecureCount: actual.newlyCovered,
      actualCoverageChanges: actual,
      plan,
      cohort: preview.cohort,
      coverageBefore,
      coverageAfter: this.coverageFor(params.environmentId, now),
      enforcement: preview.enforcement,
      implementations: this.listPolicyImplementations(
        { environmentId: params.environmentId, policyIds: params.policyIds },
        now,
      ),
    };
    if (params.previewId) {
      if (this.appliedByPreview.size >= 20) this.appliedByPreview.clear();
      this.appliedByPreview.set(params.previewId, result);
    }
    return result;
  }

  // ------------------------------------------------ policy implementation

  /**
   * One applied definition's implementation. Undefined while no visible agent
   * has the definition assigned - a recommendation has no native policies.
   */
  getPolicyImplementation(
    query: PolicyImplementationQuery,
    now = Date.now(),
  ): PolicyImplementation | undefined {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === query.environmentId,
    );
    return environment
      ? this.implementationFor(environment, query.policyId, now)
      : undefined;
  }

  listPolicyImplementations(
    query: PolicyImplementationsQuery,
    now = Date.now(),
  ): PolicyImplementation[] {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === query.environmentId,
    );
    if (!environment) return [];
    return [...new Set(query.policyIds)].flatMap(
      id => this.implementationFor(environment, id, now) ?? [],
    );
  }

  /**
   * An individual override. The parent definition, and the expected snapshot
   * it generated, are left exactly as they were.
   */
  updateNativePolicy(
    params: UpdateNativePolicyRequest,
    now = Date.now(),
  ): NativePolicy {
    const { environment, implementation } = this.requireImplementation(
      params.environmentId,
      parseNativePolicyId(params.nativePolicyId)?.definitionId,
      now,
    );
    const policy = implementation.groups
      .flatMap(group => group.policies)
      .find(item => item.id === params.nativePolicyId);
    if (!policy) throw new Error("Unknown native policy");
    if (implementation.inFlight || policy.state !== "DEPLOYED")
      throw new Error("Only a deployed native policy can be modified.");
    const current: NativePolicyLogic = {
      effect: params.current.effect,
      subject: params.current.subject.trim(),
      rule: params.current.rule.trim(),
    };
    if (!current.subject || !current.rule)
      throw new Error("A native policy needs a subject and a rule.");
    this.nativeOverrides.set(policy.id, {
      current,
      modifiedAt: new Date(now).toISOString(),
    });
    this.invalidate();
    return this.implementationFor(environment, implementation.definitionId, now)!
      .groups.flatMap(group => group.policies)
      .find(item => item.id === policy.id)!;
  }

  /**
   * Regenerate every native policy from the definition and deploy it again.
   * Refuses, rather than silently overwriting, while individual overrides
   * exist and the caller has not acknowledged them.
   */
  reapplyPolicyDefinition(
    params: ReapplyPolicyDefinitionRequest,
    now = Date.now(),
  ): PolicyImplementation {
    const { environment, implementation, blueprints } =
      this.requireImplementation(params.environmentId, params.policyId, now);
    if (implementation.inFlight)
      throw new Error("A deployment is already in progress.");
    if (implementation.counts.modified > 0 && !params.acknowledgeOverrides)
      throw new OverridesPresentError();
    return this.recordRun(
      environment,
      params.policyId,
      startRun("REAPPLY", blueprints, now),
      now,
    );
  }

  /** Redeploy only what failed; everything already deployed is left alone. */
  retryFailedDeployment(
    params: RetryPolicyDeploymentRequest,
    now = Date.now(),
  ): PolicyImplementation {
    const { environment, implementation, blueprints } =
      this.requireImplementation(params.environmentId, params.policyId, now);
    if (implementation.inFlight)
      throw new Error("A deployment is already in progress.");
    const failed = new Set(implementation.failed.map(policy => policy.id));
    if (!failed.size) throw new Error("Nothing failed to deploy.");
    return this.recordRun(
      environment,
      params.policyId,
      startRun(
        "RETRY",
        blueprints.filter(child => failed.has(child.id)),
        now,
      ),
      now,
    );
  }

  private appendRun(
    environment: AgentEnvironment,
    policyId: string,
    run: DeploymentRun,
  ): void {
    const key = implementationKey(environment.id, policyId);
    this.deploymentRuns.set(key, [
      ...(this.deploymentRuns.get(key) ?? []),
      run,
    ]);
  }

  private recordRun(
    environment: AgentEnvironment,
    policyId: string,
    run: DeploymentRun,
    now: number,
  ): PolicyImplementation {
    this.appendRun(environment, policyId, run);
    this.invalidate();
    return this.implementationFor(environment, policyId, now)!;
  }

  private requireImplementation(
    environmentId: string,
    policyId: string | undefined,
    now: number,
  ) {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    if (!environment || !policyId) throw new Error("Unknown environment");
    const resolved = this.resolveImplementation(environment, policyId, now);
    if (!resolved)
      throw new Error("This policy definition has not been applied.");
    return { environment, ...resolved };
  }

  /** What a definition is, as materialization reads it. */
  private definitionFor(
    environment: AgentEnvironment,
    policyId: string,
  ): DefinitionInput | undefined {
    const found = this.findPolicy(policyId);
    if (!found) return undefined;
    const entry = getRecommendationCatalog().find(item => item.id === policyId);
    const catalog = POLICY_CATALOG.find(item => item.id === policyId);
    return {
      id: policyId,
      name: found.name,
      environmentId: environment.id,
      outcome:
        entry?.outcome ??
        catalog?.description ??
        this.draftPolicies.get(policyId)?.intentSummary ??
        "",
      controls: this.controlsFor(policyId),
      intent: getIntentPolicies().find(item => item.id === policyId),
    };
  }

  private assignedAgents(
    environment: AgentEnvironment,
    policyId: string,
    now: number,
  ): EffectiveAgent[] {
    return this.visibleAgents(environment.id, now).filter(agent =>
      this.assignmentsFor(agent, environment).some(
        item => item.id === policyId,
      ),
    );
  }

  /** The native policies a definition generates across these agents. */
  private blueprintsFor(
    definition: DefinitionInput,
    agents: EffectiveAgent[],
    environment: AgentEnvironment,
    now: number,
  ): NativePolicyBlueprint[] {
    if (!agents.length) return [];
    const services = this.servicesByAgent(agents, environment, now);
    const byAgent = this.targetsByAgent(definition.id, agents, services);
    return materializeDefinition(
      definition,
      agents.flatMap(agent => {
        const targets = byAgent.get(agent.id);
        return targets?.applicable ? [targets] : [];
      }),
      [...services.values()].flat(),
    );
  }

  /**
   * What applying to these agents would do: the native policies their targets
   * need, created where they do not exist and updated where they do.
   */
  private planFor(
    environment: AgentEnvironment,
    policyId: string,
    agents: EffectiveAgent[],
    now: number,
  ): PolicyImplementationPlan | undefined {
    const definition = this.definitionFor(environment, policyId);
    if (!definition || !agents.length) return undefined;
    const existing = new Set(
      this.implementationFor(environment, policyId, now)?.groups.flatMap(
        group => group.policies.map(policy => policy.id),
      ) ?? [],
    );
    const blueprints = this.blueprintsFor(definition, agents, environment, now);
    return blueprints.length
      ? planFromBlueprints(policyId, blueprints, existing)
      : undefined;
  }

  private implementationFor(
    environment: AgentEnvironment,
    policyId: string,
    now: number,
  ): PolicyImplementation | undefined {
    return this.resolveImplementation(environment, policyId, now)
      ?.implementation;
  }

  private resolveImplementation(
    environment: AgentEnvironment,
    policyId: string,
    now: number,
  ) {
    const key = implementationKey(environment.id, policyId);
    const runs = this.deploymentRuns.get(key) ?? [];
    const latest = runs.at(-1);
    // Settled implementations change only on a write, which clears the memo;
    // in-flight ones change with the clock.
    const memoKey = [
      key,
      this.discoveredFor(environment, now),
      this.classifiedFor(environment, now),
      latest && now < runCompletesAt(latest) ? now : "settled",
    ].join("|");
    const memoized = this.implementationMemo.get(memoKey);
    if (memoized) return memoized;

    const definition = this.definitionFor(environment, policyId);
    const agents = this.assignedAgents(environment, policyId, now);
    if (!definition || !agents.length) return undefined;
    const appliedTimes = agents.flatMap(agent =>
      this.assignmentsFor(agent, environment)
        .filter(item => item.id === policyId)
        .map(item => Date.parse(item.appliedAt)),
    );
    const blueprints = this.blueprintsFor(definition, agents, environment, now);
    const resolved = {
      blueprints,
      implementation: buildImplementation({
        definitionId: policyId,
        definitionName: definition.name,
        environmentId: environment.id,
        blueprints,
        runs,
        baselineAt: new Date(Math.min(...appliedTimes)).toISOString(),
        overrides: this.nativeOverrides,
        now,
      }),
    };
    if (this.implementationMemo.size > 40) this.implementationMemo.clear();
    this.implementationMemo.set(memoKey, resolved);
    return resolved;
  }

  /**
   * Targets whose native policy is not deployed, as `evaluateAgent` keys them.
   * Nothing to compute until a run exists: seeded assignments predate
   * deployment tracking and read as deployed.
   */
  private undeployedTargets(
    environment: AgentEnvironment,
    policyId: string,
    now: number,
  ): Set<string> {
    if (!this.deploymentRuns.has(implementationKey(environment.id, policyId)))
      return new Set();
    const implementation = this.implementationFor(environment, policyId, now);
    return new Set(
      implementation?.groups.flatMap(group =>
        group.policies
          .filter(policy => policy.state !== "DEPLOYED")
          .map(policy => policy.targetKey),
      ) ?? [],
    );
  }

  setAccessRevocation(
    params: SetAgentAccessRevocationRequest,
  ): SetAgentAccessRevocationResult {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === params.environmentId,
    );
    if (!environment) throw new Error("Unknown environment");
    const agentIds: string[] = [];
    params.agentIds.forEach(id => {
      const agent = this.visibleAgent(params.environmentId, id, Date.now());
      if (!agent || this.revokedAgentIds.has(id) === params.accessRevoked)
        return;
      const history = this.activityFor(agent, environment);
      if (params.accessRevoked) this.revokedAgentIds.add(id);
      else this.revokedAgentIds.delete(id);
      agentIds.push(id);
      this.activity.set(id, [
        {
          id: `${id}-access-${Date.now()}-${history.length}`,
          occurredAt: new Date().toISOString(),
          kind: params.accessRevoked ? "ACCESS_SUSPENDED" : "ACCESS_RESTORED",
          action: params.accessRevoked ? "Access suspended" : "Access restored",
          service: agent.connectedResource,
          decision: "RECORDED",
          policyName: "Kill Switch",
        },
        ...history,
      ]);
    });
    this.invalidate();
    return { agentIds, accessRevoked: params.accessRevoked };
  }

  /**
   * Explicit demo/test reset to the populated fixture. Authentication uses
   * `clearSession`, which leaves the next login at day zero.
   */
  reset(): void {
    this.resetToDayZero();
    this.seedEnvironment();
  }

  /**
   * Explicitly save the never-connected state for demo scenarios and tests.
   */
  resetToDayZero(): void {
    this.loadedScope = agentSessionScope(isInvestmentBankingDemo());
    this.clearMemory();
    this.persist();
    // Stores describing estate agents (Access & Protection) never outlive
    // the estate. `reset` reseeds after this; listeners only clear their own
    // state, so notifying here covers both paths.
    notifyEstateReset();
  }

  /**
   * Connect registered MCP servers to an agent (manual association). Each id
   * binds or fails on its own; ids already reached are reported, not bound.
   */
  bindMcpServers(params: {
    environmentId: string;
    agentId: string;
    mcpServerIds: string[];
    actor: string;
  }): { bound: string[]; failed: Array<{ mcpServerId: string; reason: "NOT_REGISTERED" | "ALREADY_CONNECTED" }> } {
    const { agent, services } = this.agentServices(
      params.environmentId,
      params.agentId,
    );
    const catalog = getMcpServerCatalog();
    const bindings = [...(this.mcpBindings.get(agent.id) ?? [])];
    const result: ReturnType<AgentEstateStore["bindMcpServers"]> = {
      bound: [],
      failed: [],
    };
    const at = new Date().toISOString();
    [...new Set(params.mcpServerIds)].forEach(mcpServerId => {
      if (!catalog.some(server => server.id === mcpServerId))
        result.failed.push({ mcpServerId, reason: "NOT_REGISTERED" });
      else if (
        services.some(service => service.id === mcpServerId) ||
        bindings.some(binding => binding.serverId === mcpServerId)
      )
        result.failed.push({ mcpServerId, reason: "ALREADY_CONNECTED" });
      else {
        bindings.push({ serverId: mcpServerId, at, by: params.actor });
        result.bound.push(mcpServerId);
      }
    });
    if (result.bound.length) {
      this.mcpBindings.set(agent.id, bindings);
      this.invalidate();
    }
    return result;
  }

  /**
   * Remove one manual connection. Dependency checks (authored rules naming
   * the server) belong to the caller, which owns those rules.
   */
  unbindMcpServer(params: {
    environmentId: string;
    agentId: string;
    mcpServerId: string;
  }): void {
    const { agent, services } = this.agentServices(
      params.environmentId,
      params.agentId,
    );
    const service = services.find(item => item.id === params.mcpServerId);
    if (!service)
      throw new Error("This agent is not connected to that MCP service.");
    if (service.connectionOrigin !== "MANUAL")
      throw new Error(
        "The agent registry reports this connection; remove it there.",
      );
    const remaining = (this.mcpBindings.get(agent.id) ?? []).filter(
      binding => binding.serverId !== params.mcpServerId,
    );
    if (remaining.length) this.mcpBindings.set(agent.id, remaining);
    else this.mcpBindings.delete(agent.id);
    this.invalidate();
  }

  /**
   * Every registered MCP server, shaped as an agent would reach it (tool ids
   * match `EstateAgentMcpService`), with its MCP Registry id when known.
   */
  listMcpCatalog(): Array<
    Pick<
      EstateAgentMcpService,
      "id" | "name" | "technology" | "capabilities" | "registryServerId"
    > & {
      tools: Array<
        Pick<
          EstateAgentMcpService["tools"][number],
          "id" | "name" | "description" | "classification" | "risk"
        >
      >;
    }
  > {
    // Same tool ids as the builder an agent's services come from.
    const demo = isInvestmentBankingDemo();
    return getMcpServerCatalog().map(server => ({
      id: server.id,
      name: server.name,
      technology: server.technology,
      capabilities: [...server.capabilities],
      registryServerId: registryServerIdOf(server.id),
      tools: server.tools.map(tool => ({
        id: demo
          ? `${server.id}-${tool.name}`
          : `${server.id}-${resourceId(tool.name)}`,
        name: tool.name,
        description: toolDescription(tool.name, tool.dataAccess),
        classification: tool.classification,
        risk: TOOL_RISK[tool.classification],
      })),
    }));
  }

  private clearMemory(): void {
    this.environments = [];
    this.agentsByEnvironmentId.clear();
    this.assignments.clear();
    this.activity.clear();
    this.appliedAt.clear();
    this.revokedAgentIds.clear();
    this.mcpBindings.clear();
    this.missionDecisions.clear();
    this.draftPolicies.clear();
    this.deploymentRuns.clear();
    this.nativeOverrides.clear();
    this.importedAgentIds.clear();
    this.discoveries.clear();
    mcpServicesMemo.clear();
    this.mcpConnectivityState.clear();
    this.failedOnboarding.clear();
    this.connectionMemo.clear();
    this.implementationMemo.clear();
    this.mutationVersion = 0;
    this.appliedByPreview.clear();
    this.snapshotMemo = undefined;
    this.summaryMemo.clear();
  }

  private seedEnvironment(): void {
    const environment = buildSeededEnvironment();
    this.environments = [environment];
    this.agentsByEnvironmentId.set(environment.id, generateAgents(environment));
    this.invalidate();
  }

  /**
   * Test-only: back-date the run so specs can land in the completed state
   * without waiting out a real DISCOVERY_DURATION_MS.
   */
  backdateToComplete(): void {
    this.ensureSession();
    this.environments = this.environments.map(environment => ({
      ...environment,
      startedAt: new Date(
        Date.now() - DISCOVERY_DURATION_MS - 1_000,
      ).toISOString(),
    }));
    this.invalidate();
  }

  /**
   * Park the run mid-flight, where discovery has outrun classification.
   *
   * That gap is the only place NOT_EVALUATED agents - and the missions whose
   * inference has not run yet - actually exist, and a completed run can never
   * show either. Sibling to `backdateToComplete`, and for the same reason:
   * without it the state is unreachable except by waiting in real time.
   */
  backdateToMidRun(): void {
    this.ensureSession();
    this.environments = this.environments.map(environment => ({
      ...environment,
      startedAt: new Date(
        Date.now() - Math.round(DISCOVERY_DURATION_MS * 0.6),
      ).toISOString(),
    }));
    this.invalidate();
  }

  /**
   * Test-only: stop every environment's run at `stageId` and report it failed.
   * Sibling to `backdateToComplete`; the backend has no failure contract yet,
   * so without it the failed presentation is unreachable. `withMessage: false`
   * omits `failureMessage`, as the real backend does today.
   */
  failOnboarding(
    stageId: DiscoveryStageId = "ANALYZING",
    { withMessage = true }: { withMessage?: boolean } = {},
  ): void {
    this.ensureSession();
    const window = STAGE_WINDOWS.find(stage => stage.id === stageId)!;
    const elapsedMs = Math.round(
      ((window.start + window.end) / 2) * DISCOVERY_DURATION_MS,
    );
    this.environments = this.environments.map(environment => {
      this.failedOnboarding.set(environment.id, {
        stageId,
        elapsedMs,
        withMessage,
      });
      return {
        ...environment,
        startedAt: new Date(Date.now() - elapsedMs - 1_000).toISOString(),
      };
    });
    this.invalidate();
  }

  // ------------------------------------------------------------ internals

  private agentsFor(environmentId: string): GeneratedAgent[] {
    return this.agentsByEnvironmentId.get(environmentId) ?? [];
  }

  private discoveredFor(environment: AgentEnvironment, now: number): number {
    return discoveredCount(
      environment.totalAgents,
      elapsedMsFor(environment, now),
    );
  }

  private classifiedFor(environment: AgentEnvironment, now: number): number {
    return classifiedCount(
      environment.totalAgents,
      elapsedMsFor(environment, now),
    );
  }

  /**
   * The estate as it stands: everything discovery has found so far, with any
   * mission override applied. Every consumer - the cards, the list filter, the
   * recommendation selectors - reads agents through here, so an administrator
   * who re-targets an agent's mission re-targets all of them at once.
   */
  private visibleAgents(environmentId: string, now: number): EffectiveAgent[] {
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    if (!environment) return [];
    return this.agentsFor(environmentId)
      .slice(0, this.discoveredFor(environment, now))
      .map(agent => this.withGovernedPurpose(agent));
  }

  /**
   * Layer a confirmed mission's purpose over the discovered one. `seedPurpose`
   * keeps what discovery actually classified, because the classification
   * evidence is a record of what was observed - an administrator's override
   * changes what the agent is *governed as*, never what was seen.
   *
   * Returns the agent itself when no override applies. That is the case for
   * every agent until somebody takes a mission decision, and this runs once per
   * agent per query across the cards, the list and the detail page - copying
   * 149 records to change nothing is the one cost worth avoiding here.
   * Consumers read `seedPurpose ?? purpose` for exactly this reason.
   */
  private withGovernedPurpose(agent: GeneratedAgent): EffectiveAgent {
    const decision = this.missionDecisions.get(agent.id);
    if (!decision?.purposeCategory && !decision?.description) {
      return agent as EffectiveAgent;
    }
    return {
      ...agent,
      seedPurpose: agent.purpose,
      purpose: decision.purposeCategory ?? agent.purpose,
      purposeDescription: decision.description ?? agent.purposeDescription,
    };
  }

  /** One agent, without copying the other 148 to find it. */
  private visibleAgent(
    environmentId: string,
    agentId: string,
    now: number,
  ): EffectiveAgent | undefined {
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    if (!environment) return undefined;
    const agent = this.agentsFor(environmentId)
      .slice(0, this.discoveredFor(environment, now))
      .find(item => item.id === agentId);
    return agent ? this.withGovernedPurpose(agent) : undefined;
  }

  /**
   * Only verified required gap mappings determine evaluated coverage.
   * Catalog assignments without mappings leave those gaps unresolved.
   */
  /**
   * `includeAdvisory` adds the recommendations that are offered without being
   * coverage requirements. Only the Recommended tab asks for them: every count
   * and status reads the required set alone.
   */
  private remainingRecommendations(
    agent: EffectiveAgent,
    {
      includeAdvisory = false,
      assigned: assignedOverride,
    }: {
      includeAdvisory?: boolean;
      /**
       * Policy ids to treat as assigned instead of what the store holds - the
       * hypothetical state a preview projects. Absent, this reads the store.
       */
      assigned?: ReadonlySet<string>;
    } = {},
  ): RecommendationCatalogEntry[] {
    if (agent.settledStatus === "SECURE" && !agent.seedPurpose) return [];
    const assigned =
      assignedOverride ??
      new Set(
        (this.assignments.get(agent.id) ?? this.seededPolicies(agent)).map(
          item => item.id,
        ),
      );
    return (
      includeAdvisory ? getRecommendationCatalog() : requiredRecommendations()
    ).filter(
      entry => matchesSelector(agent, entry) && !assigned.has(entry.id),
    );
  }

  private gapsFor(
    agent: EffectiveAgent,
    assigned?: ReadonlySet<string>,
  ): AgentPolicyGap[] {
    if (agent.settledStatus === "SECURE" && !agent.seedPurpose) return [];
    const required = requiredRecommendations().filter(entry =>
      matchesSelector(agent, entry),
    );
    if (!required.length)
      return [
        {
          id: `${agent.id}:unresolved`,
          title: "Policy coverage requires review",
          description:
            "No verified policy mapping is available for this agent's purpose and resources.",
          resource: agent.connectedResource,
          toolNames: [],
        },
      ];
    return this.remainingRecommendations(agent, { assigned }).flatMap(entry =>
      entry.controls.flatMap((control, index) =>
        control.sense === "BLOCK"
          ? [
              {
                id: `${agent.id}:${entry.id}:${index}`,
                title: control.label,
                description: entry.whyRecommended,
                resource: agent.connectedResource,
                toolNames: entry.enforcement.blocked,
                recommendationId: entry.id,
              },
            ]
          : [],
      ),
    );
  }

  private seededPolicies(agent: EffectiveAgent): RecommendationCatalogEntry[] {
    return agent.settledStatus === "SECURE"
      ? getRecommendationCatalog().filter(entry =>
          matchesSelector(
            { ...agent, purpose: agent.seedPurpose ?? agent.purpose },
            entry,
          ),
        )
      : [];
  }

  private assignmentsFor(
    agent: EffectiveAgent,
    environment: AgentEnvironment,
  ): AgentPolicyAssignment[] {
    return (
      this.assignments.get(agent.id) ??
      this.seededPolicies(agent).map(entry => ({
        id: entry.id,
        name: entry.name,
        appliedAt: environment.startedAt,
      }))
    );
  }

  private activityFor(
    agent: GeneratedAgent,
    environment: AgentEnvironment,
  ): EstateAgentActivity[] {
    let events = this.activity.get(agent.id);
    if (!events) {
      events = isInvestmentBankingDemo()
        ? []
        : buildActivity(
            agent,
            agent.settledStatus,
            Date.parse(environment.startedAt),
            this.assignmentsFor(agent, environment),
          );
      this.activity.set(agent.id, events);
    }
    return events;
  }

  /** All discovered agents for adapters; avoids a second population or pagination cap. */
  snapshot(): EstateAgentDetail[] {
    this.ensureSession();
    const environment = this.environments.at(-1);
    if (!environment) return [];
    const now = Date.now();
    const key = `${environment.id}:${this.mutationVersion}:${this.discoveredFor(environment, now)}:${this.classifiedFor(environment, now)}`;
    if (this.snapshotMemo?.key === key) return this.snapshotMemo.agents;
    const agents = this.visibleAgents(environment.id, now).map(
      agent => this.getAgent(environment.id, agent.id, now)!,
    );
    this.snapshotMemo = { key, agents };
    return agents;
  }

  private effectiveStatus(
    agent: GeneratedAgent,
    classifiedCutoff: number,
    assigned?: ReadonlySet<string>,
  ): AgentSecurityStatus {
    return this.evaluate(agent as EffectiveAgent, classifiedCutoff, assigned)
      .status;
  }

  /**
   * An agent's coverage status and the gaps behind it, from one pass of the gap
   * evaluator. `assigned` swaps in a hypothetical assignment set (what a preview
   * projects); absent, it reads the store. Not-yet-classified agents have no
   * gaps to report.
   */
  private evaluate(
    agent: EffectiveAgent,
    classifiedCutoff: number,
    assigned?: ReadonlySet<string>,
  ): { status: AgentSecurityStatus; gaps: AgentPolicyGap[] } {
    if (agent.sequenceIndex >= classifiedCutoff)
      return { status: "NOT_EVALUATED", gaps: [] };
    const gaps = this.gapsFor(agent, assigned);
    return { status: gaps.length ? "NOT_SECURE" : "SECURE", gaps };
  }

  /**
   * One agent before and after a set of policies is assigned, read through the
   * SAME gap evaluator the dashboard uses. Nothing is written: the projection
   * is a hypothetical assignment set handed to the evaluator, so current
   * coverage cannot move while a review is open.
   */
  private projectAgent(
    agent: EffectiveAgent,
    classified: number,
    current: ReadonlySet<string>,
    addedPolicyIds: readonly string[],
  ): AgentProjection {
    const before = this.evaluate(agent, classified);
    const after = this.evaluate(
      agent,
      classified,
      new Set([...current, ...addedPolicyIds]),
    );
    return {
      before: { status: before.status, gapCount: before.gaps.length },
      after: { status: after.status, gapCount: after.gaps.length },
      beforeGaps: before.gaps,
    };
  }

  /** Whole-estate policy coverage, ignoring any list filter. */
  private coverageFor(environmentId: string, now: number): EstateCoverage {
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    if (!environment) {
      return {
        totalAgents: 0,
        secureCount: 0,
        notSecureCount: 0,
        notEvaluatedCount: 0,
        securePercent: 0,
      };
    }

    const classified = this.classifiedFor(environment, now);
    let secure = 0;
    let notSecure = 0;
    let notEvaluated = 0;

    const visible = this.visibleAgents(environmentId, now);
    visible.forEach(agent => {
      const status = this.effectiveStatus(agent, classified);
      if (status === "SECURE") secure += 1;
      else if (status === "NOT_EVALUATED") notEvaluated += 1;
      else notSecure += 1;
    });

    return {
      totalAgents: visible.length,
      secureCount: secure,
      notSecureCount: notSecure,
      notEvaluatedCount: notEvaluated,
      securePercent: percent(secure, visible.length),
    };
  }

  /** The one filter predicate, shared by the list and the category cards. */
  private matchesQuery(
    agent: GeneratedAgent,
    status: AgentSecurityStatus,
    recommendationCount: number,
    readiness: EnforcementReadinessStatus,
    query: Omit<EstateAgentQuery, "environmentId">,
  ): boolean {
    if (query.platform && agent.platform !== query.platform) return false;
    if (query.businessUnit && agent.businessUnit !== query.businessUnit) {
      return false;
    }
    if (query.project && agent.project !== query.project) return false;
    if (
      query.environmentType &&
      agent.environmentType !== query.environmentType
    ) {
      return false;
    }
    if (
      query.connectedResource &&
      agent.connectedResource !== query.connectedResource
    ) {
      return false;
    }
    if (query.purpose && agent.purpose !== query.purpose) return false;
    if (!matchesSecurityStatusFilter(status, query.securityStatus))
      return false;
    if (!matchesEnforcementFilter(readiness, query.enforcementStatus))
      return false;
    if (query.hasRecommendations === "true") {
      // "Has recommendations" means there is something to act on, so an
      // already-secure agent does not qualify however many entries match it.
      if (status !== "NOT_SECURE" || recommendationCount === 0) return false;
    }

    const search = String(query.q ?? "")
      .trim()
      .toLowerCase();
    if (!search) return true;
    return [
      agent.name,
      agent.purposeDescription,
      PURPOSE_LABELS[agent.purpose],
      agent.owner,
      agent.identityName,
      PLATFORM_LABELS[agent.platform],
      BUSINESS_UNIT_LABELS[agent.businessUnit],
      agent.project,
      agent.connectedResource,
    ].some(field => field.toLowerCase().includes(search));
  }

  private resolveScope(
    environmentId: string,
    scope: AgentScope,
    now = Date.now(),
  ): GeneratedAgent[] {
    const visible = this.visibleAgents(environmentId, now);
    if (scope.kind === "ALL") return visible;

    if (scope.kind === "AGENTS") {
      const wanted = new Set(scope.agentIds);
      return visible.filter(agent => wanted.has(agent.id));
    }

    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    const classified = environment
      ? this.classifiedFor(environment, now)
      : visible.length;

    if (scope.kind === "FILTER") {
      const context = readinessContext(undefined, now);
      return visible.filter(agent =>
        this.matchesQuery(
          agent,
          this.effectiveStatus(agent, classified),
          this.remainingRecommendations(agent).length,
          readinessForAgent(agent, context).status,
          scope.filters,
        ),
      );
    }

    if (scope.dimension === "NONE") return visible;

    return visible.filter(
      agent =>
        groupValue(
          agent,
          scope.dimension,
          this.effectiveStatus(agent, classified),
        ) === scope.value,
    );
  }

  /** A recommendation id, a catalog id, or a draft generated this session. */
  private findPolicy(
    policyId: string,
  ): { id: string; name: string } | undefined {
    const recommendation = getRecommendationCatalog().find(
      entry => entry.id === policyId,
    );
    if (recommendation) return recommendation;
    const catalogItem = POLICY_CATALOG.find(item => item.id === policyId);
    if (catalogItem) return catalogItem;
    const draft = this.draftPolicies.get(policyId);
    return draft ? { id: draft.id, name: draft.suggestedName } : undefined;
  }

  private evidenceFor(
    agent: EffectiveAgent,
    environment: AgentEnvironment,
  ): AgentClassificationEvidence {
    // `seedPurpose`, not `purpose`: this card explains what discovery saw. A
    // later mission override re-targets policy, but it cannot rewrite history.
    const scenario = agent.demoScenario
      ? DEMO_SCENARIOS[agent.demoScenario]
      : undefined;
    const original =
      CLASSIFICATION_EVIDENCE_CATALOG[agent.seedPurpose ?? agent.purpose];
    const base = scenario
      ? {
          ...original,
          systemPromptExcerpt: scenario.mission,
          applicationTags: [
            scenario.context,
            "Resource mapping; runtime enforcement state unknown",
          ],
        }
      : original;
    const registeredAt = new Date(
      new Date(environment.startedAt).getTime() -
        agent.createdAtOffsetDays * 86_400_000,
    );
    return {
      ...base,
      metadata: [
        { label: "Owner", value: agent.owner },
        { label: "Model", value: agent.model },
        { label: "Region", value: agent.region },
        {
          label: "Registered",
          // A date, not a machine timestamp: this row is read by a person
          // looking for context, and an ISO string reads as a bug.
          value: registeredAt.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          }),
        },
      ],
    };
  }

  private toSummary(
    agent: GeneratedAgent,
    status: AgentSecurityStatus,
    recommendedPolicyCount: number,
    environment: AgentEnvironment,
    enforcementReadiness = readinessForAgent(agent),
  ): EstateAgent {
    const startedAt = new Date(environment.startedAt).getTime();
    const appliedPolicies = this.assignmentsFor(agent, environment);
    const mcpServices = this.servicesFor(
      agent,
      status,
      appliedPolicies,
    );
    return {
      id: agent.id,
      name: agent.name,
      // The environment is the demo's agent registry (see agentRegistryStore).
      sourceIntegrationId: environment.id,
      enforcementReadiness,
      platform: agent.platform,
      businessUnit: agent.businessUnit,
      purpose: agent.purpose,
      purposeDescription: agent.purposeDescription,
      connectedResource: agent.connectedResource,
      project: agent.project,
      // The agent's own tier. Reading `environment.environmentType` here would
      // report every agent at the connection's declared tier, collapsing the
      // ENVIRONMENT grouping to a single card.
      environmentType: agent.environmentType,
      region: agent.region,
      owner: agent.owner,
      identityName: agent.identityName,
      identityProvider:
        environment.identityProvider === "CONFIGURE_LATER"
          ? PLATFORM_LABELS[agent.platform]
          : environment.identityProvider,
      securityStatus: status,
      recommendedPolicyCount,
      appliedPolicyNames: appliedPolicies.map(policy => policy.name),
      accessRevoked: this.revokedAgentIds.has(agent.id),
      mcpServiceCount: mcpServices.length,
      toolCount: mcpServices.reduce(
        (count, service) => count + service.tools.length,
        0,
      ),
      lastObserved: new Date(
        startedAt - agent.lastObservedOffsetMinutes * 60_000,
      ).toISOString(),
      // An agent classification has not reached has never been evaluated, so
      // the column stays empty rather than showing a time nothing happened at.
      lastEvaluated:
        status === "NOT_EVALUATED"
          ? undefined
          : (this.appliedAt.get(agent.id) ??
            new Date(
              startedAt - agent.lastObservedOffsetMinutes * 30_000,
            ).toISOString()),
    };
  }

  /**
   * The mission as it currently stands: a deterministic baseline derived from
   * the same seed signals the rest of the estate uses, with this session's
   * administrator decision layered on top.
   *
   * `CONFIRMED` and `OVERRIDDEN` are deliberately unreachable from the seed -
   * they are the *outcome* of a decision, so seeding them would make the flow's
   * own result indistinguishable from its starting state.
   */
  private missionFor(
    agent: EffectiveAgent,
    environment: AgentEnvironment,
    classifiedCutoff: number,
  ): AgentMission {
    const seedPurpose = agent.seedPurpose ?? agent.purpose;
    const startedAt = new Date(environment.startedAt).getTime();
    const observedAt = new Date(
      startedAt - agent.lastObservedOffsetMinutes * 60_000,
    ).toISOString();
    const inferredAt = new Date(
      startedAt - agent.createdAtOffsetDays * 86_400_000,
    ).toISOString();

    const recommendationCount = this.remainingRecommendations(agent).length;
    const unreached = isUnclassified(agent, classifiedCutoff);
    const status =
      agent.demoScenario && !unreached
        ? "INFERRED_NEEDS_REVIEW"
        : missionStatusFor(agent, classifiedCutoff, recommendationCount);
    const drifted = status === "REVIEW_RECOMMENDED";
    const unclear = status === "NEEDS_MORE_CONTEXT" && !unreached;

    const hash = fnv1a(agent.id);
    const score = unclear ? 30 + (hash % 20) : 55 + (hash % 41);
    const confidence: AgentMissionConfidence = {
      level: score >= 80 ? "HIGH" : score >= 60 ? "MEDIUM" : "LOW",
      score,
    };

    const evidence: AgentMissionEvidence[] = unreached
      ? []
      : (MISSION_EVIDENCE_CATALOG[seedPurpose] ?? []).map((item, order) => ({
          ...item,
          id: `${agent.id}-mission-evidence-${order}`,
          observedAt: item.unavailable ? undefined : observedAt,
        }));

    const contextChanges: AgentMissionContextChange[] = drifted
      ? (MISSION_CONTEXT_CHANGE_CATALOG[agent.connectedResource] ?? []).map(
          (change, order) => ({
            ...change,
            id: `${agent.id}-mission-change-${order}`,
          }),
        )
      : [];

    const suggestion = MISSION_SUGGESTION_CATALOG[seedPurpose];
    const suggestedMission: AgentMissionSuggestion | undefined =
      drifted && suggestion
        ? { ...suggestion, confidence: 60 + (hash % 25) }
        : undefined;

    const source: AgentMissionSource =
      status === "ADMINISTRATOR_DEFINED"
        ? agent.createdAtOffsetDays > 250
          ? "IMPORTED"
          : "ADMINISTRATOR"
        : "TRUSTAI_INFERRED";

    const baselineHistory: AgentMissionHistoryEntry[] = unreached
      ? []
      : [
          {
            id: `${agent.id}-mission-assessed`,
            occurredAt: inferredAt,
            action: "Mission assessed",
            detail: unclear
              ? "Signals supported more than one reading"
              : (MISSION_NAMES[seedPurpose] ?? agent.purposeDescription),
            actor: "TrustLogix",
          },
        ];

    const scenario = agent.demoScenario
      ? DEMO_SCENARIOS[agent.demoScenario]
      : undefined;
    const baseline: AgentMission = {
      name: unclear
        ? undefined
        : (scenario?.name ?? MISSION_NAMES[seedPurpose]),
      purposeCategory: unclear ? undefined : seedPurpose,
      description: unclear ? undefined : agent.purposeDescription,
      status,
      source,
      confidence: GOVERNED_MISSION_STATUSES.includes(status)
        ? undefined
        : confidence,
      evidence,
      candidates: unclear ? (MISSION_CANDIDATE_CATALOG[seedPurpose] ?? []) : [],
      synthesis: unreached
        ? undefined
        : (scenario?.context ?? MISSION_SYNTHESIS[status]),
      contextChanges,
      suggestedMission,
      history: baselineHistory,
      inferenceUnavailable: unreached || undefined,
    };

    const decision = this.missionDecisions.get(agent.id);
    if (!decision) return baseline;

    const resolved = decision.contextResolved;
    return {
      ...baseline,
      name: decision.name ?? baseline.name,
      purposeCategory: decision.purposeCategory ?? baseline.purposeCategory,
      description: decision.description ?? baseline.description,
      status: decision.status,
      source: decision.source,
      // A governed mission is not a guess, so the score is withheld once an
      // administrator has vouched for it.
      confidence: GOVERNED_MISSION_STATUSES.includes(decision.status)
        ? undefined
        : baseline.confidence,
      synthesis: MISSION_SYNTHESIS[decision.status],
      confirmedAt: decision.confirmedAt,
      confirmedBy: decision.confirmedBy,
      lastReviewedAt: decision.lastReviewedAt,
      contextChanges: resolved ? [] : baseline.contextChanges,
      suggestedMission: resolved ? undefined : baseline.suggestedMission,
      history: [...decision.history, ...baseline.history],
    };
  }

  /**
   * The one mission write. Each action is also exactly what the history entry
   * records, so the request and the audit trail can never disagree.
   */
  updateMission(
    params: UpdateAgentMissionRequest,
    now = Date.now(),
  ): UpdateAgentMissionResult | undefined {
    this.ensureSession();
    const environment = this.environments.find(
      item => item.id === params.environmentId,
    );
    if (!environment) return undefined;

    const agent = this.visibleAgent(params.environmentId, params.agentId, now);
    if (!agent) return undefined;

    const classified = this.classifiedFor(environment, now);
    const current = this.missionFor(agent, environment, classified);
    const previous = this.missionDecisions.get(agent.id);
    const occurredAt = new Date(now).toISOString();

    const entry = (
      action: string,
      detail: string,
    ): AgentMissionHistoryEntry => ({
      id: `${agent.id}-mission-${params.action.toLowerCase()}-${now}`,
      occurredAt,
      action,
      detail,
      actor: params.actor,
    });

    const prev: AgentMissionDecision = previous ?? {
      status: current.status,
      source: current.source,
      contextResolved: false,
      history: [],
    };

    let next: AgentMissionDecision;

    switch (params.action) {
      case "OVERRIDE": {
        const override = params.mission;
        if (!override) return undefined;
        next = {
          status: "OVERRIDDEN",
          source: "ADMINISTRATOR_OVERRIDE",
          name: override.name,
          purposeCategory: override.purposeCategory,
          description: override.description,
          confirmedAt: occurredAt,
          confirmedBy: params.actor,
          lastReviewedAt: occurredAt,
          contextResolved: prev.contextResolved,
          history: [
            entry(
              "Mission overridden",
              override.reason
                ? `${override.name} · ${override.reason}`
                : override.name,
            ),
            ...prev.history,
          ],
        };
        break;
      }
      case "ACCEPT_SUGGESTED": {
        const suggested = current.suggestedMission;
        if (!suggested) return undefined;
        next = {
          status: "OVERRIDDEN",
          source: "ADMINISTRATOR_OVERRIDE",
          name: suggested.name,
          purposeCategory: suggested.purposeCategory,
          description: suggested.description,
          confirmedAt: occurredAt,
          confirmedBy: params.actor,
          lastReviewedAt: occurredAt,
          contextResolved: true,
          history: [
            entry("Mission updated after context change", suggested.name),
            ...prev.history,
          ],
        };
        break;
      }
      case "KEEP_CURRENT":
        next = {
          ...prev,
          status: "CONFIRMED",
          name: prev.name ?? current.name,
          purposeCategory: prev.purposeCategory ?? current.purposeCategory,
          description: prev.description ?? current.description,
          confirmedAt: prev.confirmedAt ?? occurredAt,
          confirmedBy: prev.confirmedBy ?? params.actor,
          lastReviewedAt: occurredAt,
          contextResolved: true,
          history: [
            entry(
              "Context change reviewed",
              `Mission kept as ${current.name ?? "current mission"}`,
            ),
            ...prev.history,
          ],
        };
        break;
      case "NEEDS_MORE_CONTEXT":
        next = {
          // Carries `prev` forward untouched apart from the review stamp:
          // declining to confirm must not write down the mission the
          // assessment just declined to state.
          ...prev,
          status: "NEEDS_MORE_CONTEXT",
          lastReviewedAt: occurredAt,
          history: [
            entry(
              "Mission review deferred",
              "Not enough information to confirm a mission",
            ),
            ...prev.history,
          ],
        };
        break;
      case "CONFIRM":
      default:
        next = {
          ...prev,
          status: "CONFIRMED",
          name: prev.name ?? current.name,
          purposeCategory: prev.purposeCategory ?? current.purposeCategory,
          description: prev.description ?? current.description,
          confirmedAt: occurredAt,
          confirmedBy: params.actor,
          lastReviewedAt: occurredAt,
          history: [
            entry(
              "Mission confirmed",
              current.name ?? "TrustLogix assessment accepted",
            ),
            ...prev.history,
          ],
        };
        break;
    }

    this.missionDecisions.set(agent.id, next);
    this.invalidate();

    // No need to re-resolve the agent: `missionFor` layers whatever is in
    // `missionDecisions` over the baseline, and that is what just changed.
    return {
      agentId: agent.id,
      action: params.action,
      mission: this.missionFor(agent, environment, classified),
    };
  }

  /**
   * Every MCP service one agent reaches, evaluated: what discovery reports,
   * then what an administrator connected - one list for detail, summary
   * counts, enforcement and policy targets, so they never disagree.
   * (`reachedServices` is the same set, unevaluated, for readiness.)
   */
  private servicesFor(
    agent: GeneratedAgent,
    status: AgentSecurityStatus,
    policies: PolicyReference[],
  ): EstateAgentMcpService[] {
    const revoked = this.revokedAgentIds.has(agent.id);
    const bindings = this.mcpBindings.get(agent.id);
    // Almost every agent: discovery only, nothing to merge or stamp.
    if (!bindings?.length)
      return buildMcpServices(agent, status, policies, revoked);
    const catalog = getMcpServerCatalog();
    const connected = bindings.flatMap(binding => {
      const server = catalog.find(item => item.id === binding.serverId);
      return server ? [server] : [];
    });
    return buildMcpServices(agent, status, policies, revoked, connected).map(
      service => {
        const binding = bindings.find(item => item.serverId === service.id);
        return binding
          ? {
              ...service,
              connectionOrigin: "MANUAL" as const,
              connectedAt: binding.at,
              connectedBy: binding.by,
            }
          : service;
      },
    );
  }

  /**
   * The MCP services an agent reaches, by id and name - discovered or
   * manually connected - for the paths that need no evaluation (readiness,
   * client-profile usage). Discovery stays memoized; connections add to it.
   */
  private reachedServices(
    agent: GeneratedAgent,
  ): Array<Pick<EstateAgentMcpService, "id" | "name">> {
    const discovered = mcpServicesOf(agent);
    const bindings = this.mcpBindings.get(agent.id);
    if (!bindings?.length) return discovered;
    const catalog = getMcpServerCatalog();
    return [
      ...discovered,
      ...bindings.flatMap(binding => {
        const server = catalog.find(item => item.id === binding.serverId);
        return server && !discovered.some(({ id }) => id === server.id)
          ? [{ id: server.id, name: server.name }]
          : [];
      }),
    ];
  }

  /**
   * One agent's MCP services without building its whole detail - what the
   * connection reads and writes need.
   */
  private agentServices(environmentId: string, agentId: string) {
    this.ensureSession();
    const now = Date.now();
    const environment = this.environments.find(
      item => item.id === environmentId,
    );
    const agent = environment && this.visibleAgent(environmentId, agentId, now);
    if (!environment || !agent)
      throw new Error("This agent is not part of the environment.");
    const status = this.effectiveStatus(
      agent,
      this.classifiedFor(environment, now),
    );
    return {
      agent,
      services: this.servicesFor(
        agent,
        status,
        this.assignmentsFor(agent, environment),
      ),
    };
  }

  private invalidate(): void {
    this.mutationVersion += 1;
    this.snapshotMemo = undefined;
    this.summaryMemo.clear();
    this.enforcementMemo.clear();
    this.implementationMemo.clear();
    this.persist();
  }
}

/* ------------------------------------------------------------- helpers */

const implementationKey = (environmentId: string, policyId: string) =>
  `${environmentId}::${policyId}`;

const percent = (part: number, total: number): number =>
  total ? Math.round((part / total) * 100) : 0;

const strengthRank = (entry: AgentPolicyRecommendation): number =>
  entry.strength === "STRONGLY_RECOMMENDED" ? 1 : 0;

/** Part of the summary memo key: two different filters are two different cards. */
/** A demo agent's registry is its environment (see `sourceIntegrationId`). */
function outsideRegistry(
  query: Pick<EstateAgentQuery, "agentRegistryId">,
  environmentId: string,
): boolean {
  return !!query.agentRegistryId && query.agentRegistryId !== environmentId;
}

function queryFingerprint(
  query: Omit<EstateAgentQuery, "environmentId">,
): string {
  return [
    query.q ?? "",
    query.platform ?? "",
    query.businessUnit ?? "",
    query.project ?? "",
    query.environmentType ?? "",
    query.connectedResource ?? "",
    query.purpose ?? "",
    query.agentRegistryId ?? "",
    query.securityStatus ?? "",
    query.hasRecommendations ?? "",
    query.enforcementStatus ?? "",
  ].join("|");
}

/**
 * Exhaustive by construction: the `never` default makes a newly added
 * `AgentGroupDimension` a compile error here. It used to fall through to
 * `connectedResource`, which meant a new dimension silently grouped by the
 * wrong field and produced plausible-looking, wrong cards.
 */
/** Every lens a group card can be keyed by, i.e. `AgentEstateGroup.key`. */
const ENFORCEMENT_GROUP_DIMENSIONS: AgentGroupDimension[] = [
  "BUSINESS_UNIT",
  "PROJECT",
  "ENVIRONMENT",
  "PLATFORM",
  "PURPOSE",
  "CONNECTED_RESOURCE",
  "SECURITY_STATUS",
];

function groupValue(
  agent: GeneratedAgent,
  dimension: AgentGroupDimension,
  status: AgentSecurityStatus,
): string {
  switch (dimension) {
    case "NONE":
      return "ALL";
    case "PLATFORM":
      return agent.platform;
    case "BUSINESS_UNIT":
      return agent.businessUnit;
    case "PROJECT":
      return agent.project;
    case "ENVIRONMENT":
      return agent.environmentType;
    case "PURPOSE":
      return agent.purpose;
    case "SECURITY_STATUS":
      return status;
    case "CONNECTED_RESOURCE":
      return agent.connectedResource;
    default: {
      const exhaustive: never = dimension;
      return exhaustive;
    }
  }
}


/** Enforcement readiness across a set of agents. */
function tallyAgents(
  agents: GeneratedAgent[],
  context: ReturnType<typeof readinessContext>,
) {
  const counts = emptyReadinessCounts();
  agents.forEach(agent =>
    tallyReadiness(counts, readinessForAgent(agent, context).status),
  );
  return counts;
}

/**
 * The recommendations that are coverage requirements. Advisory ones are still
 * offered on the Recommended tab, but never open a gap or gate Policy Covered -
 * every status and count reads this list.
 */
const requiredRecommendations = () =>
  getRecommendationCatalog().filter(entry => !entry.advisory);

/**
 * "8 Engineering agents · GitHub" when the matched agents share one business
 * unit, and the count plus the technology otherwise - never a label that
 * claims more homogeneity than the selection has.
 */
function composeScopeLabel(
  matched: GeneratedAgent[],
  entry: RecommendationCatalogEntry,
): string {
  const units = new Set(matched.map(agent => agent.businessUnit));
  const noun =
    units.size === 1
      ? `${BUSINESS_UNIT_LABELS[[...units][0]]} agents`
      : "agents";
  return `${matched.length} ${noun} · ${entry.technology}`;
}

/**
 * The mission's starting state, derived entirely from signals the estate
 * already generates - no new randomness, so one environment always yields the
 * same missions.
 *
 * CONFIRMED and OVERRIDDEN are absent on purpose: they are the outcome of an
 * administrator decision, and seeding them would make the flow's own result
 * indistinguishable from where it started.
 */
/**
 * Classification has not reached this agent yet. The one definition, shared by
 * the status derivation and the mission builder - the two must agree about
 * which agents have nothing to assess.
 */
function isUnclassified(
  agent: GeneratedAgent,
  classifiedCutoff: number,
): boolean {
  return agent.sequenceIndex >= classifiedCutoff;
}

function missionStatusFor(
  agent: GeneratedAgent,
  classifiedCutoff: number,
  recommendationCount: number,
): AgentMissionStatus {
  // Nothing to assess yet - the mirror of NOT_EVALUATED, and it self-heals as
  // the run advances.
  if (isUnclassified(agent, classifiedCutoff)) return "NEEDS_MORE_CONTEXT";
  if (agent.settledStatus === "SECURE") return "ADMINISTRATOR_DEFINED";
  // Nothing in the catalog matched, which *is* "TrustLogix cannot tell what
  // this is" - stating a mission here would be the inference it cannot make.
  if (recommendationCount === 0) return "NEEDS_MORE_CONTEXT";
  // A custom tool on the agent's own MCP server is a capability that went
  // beyond the out-of-the-box catalog after the mission was last confirmed -
  // the plausible sign of drift. (Each agent resolves to exactly one server,
  // so "reaching the most services" is no longer a meaningful signal here.)
  if (resolveMcpServer(agent)?.tools.some(tool => tool.custom))
    return "REVIEW_RECOMMENDED";
  return "INFERRED_NEEDS_REVIEW";
}

function addTo(map: Map<string, Set<string>>, key: string, value: string) {
  const set = map.get(key) ?? new Set<string>();
  set.add(value);
  map.set(key, set);
}

/** A legacy plan, told how many of its records will be created, updated or left alone. */
function withOperations(
  plan: PolicyImplementationPlan,
  counts: PlanOperationCounts,
): PolicyImplementationPlan {
  return {
    ...plan,
    updatedCount: counts.update,
    createdCount: counts.create,
    unchangedCount: counts.unchanged,
    skippedCount: counts.skipped,
    pendingClassificationCount: counts.pending,
  };
}

function emptyAttentionIds(): AttentionAgentIds {
  return { policyGap: [], setupGap: [], notEvaluated: [], unknownReadiness: [] };
}

/**
 * Files one agent under every attention bucket it belongs to. The buckets
 * overlap on purpose - an agent can have a policy gap and a setup gap at once -
 * so a total is the union of the ids, never the sum of the buckets.
 */
function unprotectedIn(group: AgentEstateGroup): number {
  return group.totalAgents - (group.protectedCount ?? 0);
}

function addAttention(
  ids: AttentionAgentIds,
  agentId: string,
  status: AgentSecurityStatus,
  readiness: EnforcementReadinessStatus,
): void {
  if (status === "NOT_SECURE") ids.policyGap.push(agentId);
  if (status === "NOT_EVALUATED") ids.notEvaluated.push(agentId);
  if (readiness === "SETUP_REQUIRED" || readiness === "ATTENTION_REQUIRED")
    ids.setupGap.push(agentId);
  if (readiness === "UNKNOWN") ids.unknownReadiness.push(agentId);
}

function mostFrequent<T>(values: T[]): T | undefined {
  const counts = new Map<T, number>();
  values.forEach(value => counts.set(value, (counts.get(value) ?? 0) + 1));
  return [...counts.entries()].sort(
    (left, right) => right[1] - left[1],
  )[0]?.[0];
}

function summariseIntent(
  intent: string,
  controls: DraftPolicyControl[],
): string {
  const allowed = controls.filter(control => control.sense === "ALLOW").length;
  const blocked = controls.filter(control => control.sense === "BLOCK").length;
  if (!intent) return "No intent was provided.";
  if (allowed && blocked) {
    return `Allow the described activity while preventing ${blocked} higher-risk action${blocked === 1 ? "" : "s"}.`;
  }
  if (blocked) {
    return `Prevent ${blocked} action${blocked === 1 ? "" : "s"} described in the intent.`;
  }
  return `Allow ${allowed} activit${allowed === 1 ? "y" : "ies"} described in the intent.`;
}

/**
 * Names the draft after the outcome, matching how every catalog policy is
 * named - the first thing it prevents, or what it permits when it prevents
 * nothing.
 */
function suggestDraftName(
  controls: DraftPolicyControl[],
  technology: DraftPolicy["technology"],
): string {
  const qualifier = technology === "Any" ? "" : ` for ${technology}`;
  const firstBlock = controls.find(control => control.sense === "BLOCK");
  if (!firstBlock) {
    return `Allow approved agent activity${qualifier}`;
  }
  const outcome = firstBlock.label
    .replace(/^Deny /, "Prevent ")
    .replace(/^Require /, "Require ");
  return `${outcome}${qualifier}`;
}

function sortAgents(
  agents: EstateAgent[],
  sortBy?: string,
  sortOrder?: "ASC" | "DESC",
): EstateAgent[] {
  if (!sortBy) return agents;
  const direction = sortOrder === "DESC" ? -1 : 1;
  return [...agents].sort((left, right) => {
    const leftValue = String(left[sortBy as keyof EstateAgent] ?? "");
    const rightValue = String(right[sortBy as keyof EstateAgent] ?? "");
    return leftValue.localeCompare(rightValue) * direction;
  });
}

const TOOL_RISK: Record<
  EstateAgentToolClassification,
  "Low" | "Medium" | "High"
> = {
  Read: "Low",
  Write: "Medium",
  Privileged: "High",
  Destructive: "High",
};

const resourceId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

interface PolicyReference {
  id: string;
  name: string;
}

/**
 * The one MCP server an agent reaches: a scoped match on the agent's own
 * business unit wins over a technology-wide fallback (`businessUnits: []`).
 * Every agent in the generated estate resolves to exactly one server, so
 * `buildMcpServices` no longer has to walk a cross-technology order.
 */
function resolveMcpServer(agent: GeneratedAgent): McpServerDefinition | undefined {
  const candidates = getMcpServerCatalog().filter(
    server => server.technology === agent.connectedResource,
  );
  return (
    candidates.find(server => server.businessUnits.includes(agent.businessUnit)) ??
    candidates.find(server => server.businessUnits.length === 0) ??
    candidates[0]
  );
}

/**
 * Build one coherent relationship graph for both the list summary and detail.
 *
 * Discovery resolves each agent to one MCP server by its technology and
 * business unit; `connected` adds the servers an administrator bound. The list
 * summary's `mcpServiceCount`/`toolCount` and the detail's tool lists come from
 * the same call, so they can never disagree.
 */
function buildMcpServices(
  agent: GeneratedAgent,
  status: AgentSecurityStatus,
  policies: PolicyReference[],
  accessRevoked: boolean,
  connected: McpServerDefinition[] = [],
): EstateAgentMcpService[] {
  if (isInvestmentBankingDemo())
    return buildDemoMcpServices(
      agent,
      status,
      policies,
      accessRevoked,
      resolveMcpServer(agent),
      connected,
    ).map(service => ({
      ...service,
      registryServerId:
        service.registryServerId ?? registryServerIdOf(service.id),
    }));
  const discovered = resolveMcpServer(agent);
  return withConnected(discovered ? [discovered] : [], connected).map(server =>
    buildMcpService(agent, server, status, policies, accessRevoked),
  );
}

/**
 * The MCP Registry record behind a catalog server, when an Intent Policy maps
 * one - what "View in registry" deep-links to.
 */
function registryServerIdOf(serverId: string): string | undefined {
  for (const policy of getIntentPolicies()) {
    const id = policy.mcp[serverId]?.registryServerId;
    if (id) return id;
  }
  return undefined;
}

/** One server as one agent reaches it - the same evaluation for every origin. */
function buildMcpService(
  agent: GeneratedAgent,
  server: McpServerDefinition,
  status: AgentSecurityStatus,
  policies: PolicyReference[],
  accessRevoked: boolean,
): EstateAgentMcpService {
  const tools = server.tools;
  const policy = policies.find(assignment =>
    getRecommendationCatalog().some(
      entry =>
        entry.id === assignment.id &&
        (!entry.selector.technologies ||
          entry.selector.technologies.includes(server.technology)),
    ),
  );
  const required = requiredRecommendations().filter(entry =>
    matchesSelector(agent, entry),
  );
  const resourceCovered =
    status !== "NOT_EVALUATED" &&
    !!policy &&
    required.every(entry =>
      policies.some(assignment => assignment.id === entry.id),
    );
  const serviceId = server.id;
  const serviceStatus = accessRevoked
    ? ("DENIED" as const)
    : resourceCovered
      ? ("PROTECTED" as const)
      : status === "NOT_SECURE"
        ? ("NEEDS_POLICY" as const)
        : ("REVIEW" as const);

  return {
      id: serviceId,
      name: server.name,
      registryServerId: registryServerIdOf(serviceId),
      technology: server.technology,
      capabilities: server.capabilities,
      dataAccess: [...new Set(tools.map(tool => tool.dataAccess))],
      protectionStatus: serviceStatus,
      tools: tools.map(tool => {
        const secureAccess =
          tool.classification === "Read"
            ? ("ALLOWED" as const)
            : tool.classification === "Destructive"
              ? ("DENIED" as const)
              : ("RESTRICTED" as const);
        const secureProtection =
          tool.classification === "Privileged"
            ? ("RESTRICTED" as const)
            : tool.classification === "Destructive"
              ? ("DENIED" as const)
              : ("PROTECTED" as const);
        const protectionStatus = accessRevoked
          ? ("DENIED" as const)
          : resourceCovered
            ? secureProtection
            : status === "NOT_SECURE"
              ? ("NEEDS_POLICY" as const)
              : ("REVIEW" as const);

        return {
          id: `${serviceId}-${resourceId(tool.name)}`,
          name: tool.name,
          description: toolDescription(tool.name, tool.dataAccess),
          classification: tool.classification,
          risk: TOOL_RISK[tool.classification],
          dataAccess: tool.dataAccess,
          access: accessRevoked
            ? ("DENIED" as const)
            : resourceCovered
              ? secureAccess
              : ("ALLOWED" as const),
          protectionStatus,
          policyId: resourceCovered ? policy?.id : undefined,
          policyName: resourceCovered ? policy?.name : undefined,
        };
      }),
    };
}

function buildActivity(
  agent: GeneratedAgent,
  status: AgentSecurityStatus,
  startedAt: number,
  policies: AgentPolicyAssignment[],
): EstateAgentActivity[] {
  const service = agent.connectedResource;
  const server = resolveMcpServer(agent);
  const tools = server?.tools ?? [];
  return tools.slice(0, 3).map((tool, index) => ({
    id: `${agent.id}-activity-${index}`,
    occurredAt: new Date(startedAt - (index + 1) * 37 * 60_000).toISOString(),
    kind: "RUNTIME",
    requestId: `${agent.id}-request-${index}`,
    enforcementPoint: server?.name ?? `${service} MCP`,
    policyId: policies[0]?.id,
    action: tool.name,
    governanceViolation:
      status === "NOT_SECURE" && tool.classification !== "Read",
    service,
    decision:
      status === "NOT_EVALUATED"
        ? ("WARNING" as const)
        : status === "SECURE" && tool.classification !== "Read"
          ? ("DENIED" as const)
          : ("ALLOWED" as const),
    policyName:
      status === "NOT_EVALUATED"
        ? "Not yet evaluated"
        : status === "SECURE"
          ? (policies[0]?.name ?? "Enterprise Access Control Baseline")
          : "No policy in effect",
  }));
}

/** The environment an onboarding request (or its discovery) will create. */
function pendingEnvironment(
  params: Pick<
    StartAgentOnboardingRequest,
    "name" | "platform" | "identityProvider" | "environmentType"
  >,
): Omit<AgentEnvironment, "totalAgents"> {
  // The wizard no longer asks for the connection's tier; production is what
  // an agent registry connection is unless the request says otherwise.
  return {
    id: isInvestmentBankingDemo()
      ? `demo-${params.platform.toLowerCase()}-${fnv1a(params.name)}`
      : `agent-environment-${Date.now()}`,
    name: params.name,
    platform: params.platform,
    identityProvider: params.identityProvider,
    environmentType: params.environmentType ?? "PRODUCTION",
    health: "HEALTHY",
    startedAt: new Date().toISOString(),
  };
}

/**
 * The MCP Services an agent references, as discovery sees them: before any
 * classification or policy. Same builder as the estate, so the services the
 * wizard lists are the services the imported agents reach.
 */
const mcpServicesMemo = new Map<string, EstateAgentMcpService[]>();

/**
 * Generated agents are never mutated, so each one's discovered services are
 * built once. Discovery only: manual connections are the store's
 * (`servicesFor`), and discovery never reports them.
 */
const mcpServicesOf = (agent: GeneratedAgent): EstateAgentMcpService[] => {
  const key = `${isInvestmentBankingDemo()}:${agent.id}`;
  let services = mcpServicesMemo.get(key);
  if (!services) {
    services = buildMcpServices(agent, "NOT_EVALUATED", [], false);
    mcpServicesMemo.set(key, services);
  }
  return services;
};

const mcpServiceIdsOf = (agents: GeneratedAgent[]): Set<string> =>
  new Set(agents.flatMap(agent => mcpServicesOf(agent).map(({ id }) => id)));

const store = new AgentEstateStore();
registerReadinessPathResolver((agent, now) =>
  store.connectionPathsForReadiness(agent, now),
);
registerAgentSessionReset("estate", () => store.clearSession());

export const agentEstateStore = store;
